import { DatePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { distinctUntilChanged, skip, Subscription, forkJoin } from 'rxjs';
import { toDataURL } from 'qrcode';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { AuthService } from '../../../core/auth/auth.service';
import { UserRole } from '../../../core/auth/user-role.type';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import { LoadingBlockComponent } from '../../../shared/ui/loading-block/loading-block.component';
import { EmptyStateComponent } from '../../../shared/ui/empty-state/empty-state.component';
import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import { CompaniesService } from '../../companies/data-access/companies.service';
import { CompanyItem } from '../../companies/data-access/companies.models';
import {
  ClientAdminItem,
  ClientCommunicationChannels,
} from '../../clients/data-access/clients-admin.models';
import {
  ClientCommunicationSummaryView,
  presentClientCommunicationChannels,
} from '../../clients/data-access/client-communication.presenter';
import { ClientsAdminService } from '../../clients/data-access/clients-admin.service';
import { ProcedureTypeItem } from '../../procedure-types/data-access/procedure-types.models';
import { ProcedureTypesService } from '../../procedure-types/data-access/procedure-types.service';
import { SettingsService } from '../../settings/data-access/settings.service';
import { TelegramConnectorService } from '../../telegram/data-access/telegram-connector.service';
import {
  TelegramLinkDto,
  TelegramLinkTokenDto,
} from '../../telegram/data-access/telegram-connector.models';
import {
  ClinicOperationalSettingsDto,
  CompanyRatingSettingsDto,
} from '../../settings/data-access/settings.models';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import {
  ActionMenuComponent,
  DataTableShellComponent,
  DateFieldComponent,
  ErrorBlockComponent,
  FloatingActionFabComponent,
  HoverIdComponent,
  ListFilterShortcutDirective,
  ModalKeyboardDirective,
  PageActionModalComponent,
  PageContextGuardComponent,
  PageShellComponent,
  TabStripComponent,
  TabStripItem,
} from '@clinic-platform/ui-components';
import {
  ManualAdjustUserClientRatingRequest,
  StaffRole,
  UserClientRatingEvent,
  UserItem
} from '../data-access/users.models';
import { UsersService } from '../data-access/users.service';

type UsersStatusFilter = '' | 'active' | 'inactive';
type ClientAccountStatusFilter = '' | 'no_account' | 'invited' | 'registered';
type ClientEmailPresenceFilter = '' | 'with_email' | 'without_email';
type ClientOriginFilter = '' | 'self_registration' | 'invite' | 'offline_only';
type UsersSort = 'createdAtDesc' | 'createdAtAsc';
type UserManagementFilterState = {
  query: string;
  userStatus: UsersStatusFilter;
  clientAccountStatus: ClientAccountStatusFilter;
  clientEmailPresence: ClientEmailPresenceFilter;
  clientOrigin: ClientOriginFilter;
  sort: UsersSort;
};

@Component({
  selector: 'app-users-page',
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    TranslatePipe,
    PageHeaderComponent,
    LoadingBlockComponent,
    EmptyStateComponent,
    CompanyContextRequiredComponent,
    DateFieldComponent,
    ActionMenuComponent,
    ErrorBlockComponent,
    FloatingActionFabComponent,
    HoverIdComponent,
    ListFilterShortcutDirective,
    PageContextGuardComponent,
    PageActionModalComponent,
    DataTableShellComponent,
    TabStripComponent,
    ModalKeyboardDirective,
    PageShellComponent,
  ],
  template: `
    <lib-page-shell
      libListFilterShortcut
      [libListFilterShortcut]="!companyContextMissing"
      (libListFilterShortcutOpen)="openFiltersModal()"
    >
      <app-page-header
        title="User Management"
        subtitle="Create test users for role and guard validation"
      />

    <lib-tab-strip
      [items]="roleTabs"
      [activeId]="activeRoleTab"
      (activeIdChange)="selectRoleTab($event)"
      ariaLabel="Users role filter"
    />

    <lib-floating-action-fab
      [menuTemplate]="usersActionsMenu"
      buttonAriaLabel="Open user management actions"
      [disabled]="companyContextMissing"
    />

    <ng-template #usersActionsMenu let-close="close">
      <div class="users-page__actions-menu">
        <button
          type="button"
          role="menuitem"
          class="action-menu__item"
          [disabled]="!canCreateForActiveRole || isSubmitting"
          (click)="openCreateModal(); close()"
        >
          {{ createButtonText | t }}
        </button>
        <button
          type="button"
          role="menuitem"
          class="action-menu__item"
          (click)="openFiltersModal(); close()"
        >
          Filters & Sort (Ctrl+F)
        </button>
        @if (hasActiveFilters) {
          <button
            type="button"
            role="menuitem"
            class="action-menu__item"
            (click)="resetFilters(); close()"
          >
            Reset
          </button>
        }
        <button
          type="button"
          role="menuitem"
          class="action-menu__item"
          (click)="load(); close()"
        >
          Refresh
        </button>
      </div>
    </ng-template>

    <lib-page-action-modal
      [isOpen]="isFiltersModalOpen"
      title="Filters & Sort"
      subtitle="User management list controls"
      width="760px"
      [canSubmit]="true"
      (close)="closeFiltersModal()"
      (submit)="applyFilters(); closeFiltersModal()"
    >
      <form
        [formGroup]="filtersForm"
        class="users-page__filters-modal"
        (submit)="$event.preventDefault(); applyFilters(); closeFiltersModal()"
      >
        <div class="users-page__filters-grid">
          <div class="users-page__field">
            <label for="users-filter-query">Search</label>
            <input
              id="users-filter-query"
              type="text"
              formControlName="query"
              placeholder="Name, phone, email"
              data-modal-primary-focus="true"
            />
          </div>

          @if (isClientTabActive) {
            <div class="users-page__field">
              <label for="users-client-status-filter">Account status</label>
              <select id="users-client-status-filter" formControlName="clientAccountStatus">
                <option value="">All</option>
                <option value="no_account">No account</option>
                <option value="invited">Invited</option>
                <option value="registered">Registered</option>
              </select>
            </div>

            <div class="users-page__field">
              <label for="users-client-email-filter">Email</label>
              <select id="users-client-email-filter" formControlName="clientEmailPresence">
                <option value="">All</option>
                <option value="with_email">With email</option>
                <option value="without_email">Without email</option>
              </select>
            </div>

            <div class="users-page__field">
              <label for="users-client-origin-filter">Origin</label>
              <select id="users-client-origin-filter" formControlName="clientOrigin">
                <option value="">All</option>
                <option value="offline_only">Offline only</option>
                <option value="invite">Invite</option>
                <option value="self_registration">Self registration</option>
              </select>
            </div>
          } @else {
            <div class="users-page__field">
              <label for="users-status-filter">Status</label>
              <select id="users-status-filter" formControlName="userStatus">
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          }

          <div class="users-page__field">
            <label for="users-sort-filter">Sort</label>
            <select id="users-sort-filter" formControlName="sort">
              <option value="createdAtDesc">Newest created</option>
              <option value="createdAtAsc">Oldest created</option>
            </select>
          </div>
        </div>

        <div class="users-page__modal-actions">
          <button type="button" class="users-page__action" (click)="resetFilters()">
            Reset
          </button>
          <button type="submit" class="users-page__action users-page__action--primary">
            Apply Filters
          </button>
        </div>
      </form>
    </lib-page-action-modal>

    @if (actionErrorText && !isCreateModalOpen) {
      <lib-error-block variant="error" [message]="(actionErrorText | t) ?? ''" />
    }

    @if (successText && !isCreateModalOpen) {
      <lib-error-block variant="success" [message]="successText" />
    }

    @if (isCreateModalOpen) {
      <div class="users-page__modal-backdrop" (click)="closeCreateModal()">
        <div
          class="users-page__modal"
          (click)="$event.stopPropagation()"
          [libModalKeyboard]="true"
          [libModalKeyboardCanSubmit]="canSubmitModal"
          [libModalKeyboardCanCancel]="!isSubmitting"
          (libModalKeyboardCancel)="closeCreateModal()"
          (libModalKeyboardSubmit)="onSubmit()"
        >
          <h3 class="users-page__modal-title">{{ modalTitle | t }}</h3>
          <form [formGroup]="form" (ngSubmit)="onSubmit()" class="users-page__grid">
            @if (isSystemAdmin) {
              <div class="users-page__field">
                <label for="users-company-id">{{ 'Company' | t }}</label>
                <select id="users-company-id" formControlName="companyId" [disabled]="!isCreateMode || isSubmitting">
                  <option value="">Select company</option>
                  @for (company of companies; track company.companyId) {
                    <option [value]="company.companyId">
                      {{ company.slug }} - {{ company.legalName }}
                    </option>
                  }
                </select>
              </div>
            }

            @if (!isSelectedClientEntity) {
            <div class="users-page__field">
              <label for="users-email">{{ 'auth.email' | t }}</label>
              <input id="users-email" type="email" formControlName="email" data-modal-primary-focus="true" [readonly]="isViewMode || isSubmitting" />
            </div>
            }

            <div class="users-page__field">
              <label for="users-phone">{{ 'Phone' | t }}</label>
              <input id="users-phone" type="tel" formControlName="phone" [readonly]="isViewMode || isSubmitting" />
            </div>

            @if (isCreateClientContactMissing) {
              <div class="users-page__inline-error users-page__field--wide">
                {{ 'users.error.clientContactRequired' | t }}
              </div>
            }

            @if (!isCreateMode && !isSelectedClientEntity) {
              <div class="users-page__field">
                <label for="users-password">{{ 'auth.password' | t }}</label>
                <input id="users-password" type="text" formControlName="password" [readonly]="isViewMode || isSubmitting" />
              </div>
            }

            <div class="users-page__field">
              <label for="users-full-name">{{ 'Full name' | t }}</label>
              <input id="users-full-name" type="text" formControlName="fullName" [readonly]="isViewMode || isSubmitting" />
            </div>

            <div class="users-page__field">
              <label for="users-birth-date">{{ 'Birth date' | t }}</label>
              <lib-date-field
                inputId="users-birth-date"
                formControlName="birthDate"
                [readonly]="isViewMode || isSubmitting"
              />
            </div>

            @if ((isCreateMode && isCreateClientRole) || isSelectedClientEntity) {
              <div class="users-page__field users-page__field--wide">
                <label for="users-notes">{{ 'Notes' | t }}</label>
                <input id="users-notes" type="text" formControlName="notes" [readonly]="isViewMode || isSubmitting" />
              </div>
            }

            @if (!isSelectedClientEntity) {
            <div class="users-page__field">
              <label for="users-role">{{ 'Role' | t }}</label>
              <select id="users-role" formControlName="role" [disabled]="!isCreateMode || isSubmitting">
                @for (role of creatableRoles; track role) {
                  <option [value]="role">{{ ('role.' + role) | t }}</option>
                }
              </select>
            </div>
            }

            @if (!isCreateMode && selectedUser && canShowSelectedUserPhoto) {
              <div class="users-page__field users-page__field--wide">
                <label>Profile Photo</label>
                <div class="users-page__photo-block">
                  @if (selectedUser.photoUrl) {
                    <img
                      [src]="selectedUser.photoUrl"
                      alt="User profile photo"
                      class="users-page__photo-image"
                    />
                  } @else {
                    <div class="users-page__photo-placeholder">No photo uploaded</div>
                  }

                  @if (isEditMode && canEditModal) {
                    <div class="users-page__photo-controls">
                      <input
                        type="file"
                        accept="image/*"
                        [disabled]="isUserPhotoUploading || isUserPhotoRemoving"
                        (change)="onSelectedUserPhotoChange($event)"
                      />

                      @if (selectedUserPhotoName) {
                        <div class="users-page__account-meta">Selected: {{ selectedUserPhotoName }}</div>
                      }

                      <div class="users-page__client-actions-row">
                        <button
                          type="button"
                          class="users-page__action users-page__action--primary"
                          [disabled]="!selectedUserPhotoFile || isUserPhotoUploading || isUserPhotoRemoving"
                          (click)="uploadSelectedUserPhoto()"
                        >
                          {{ isUserPhotoUploading ? 'Uploading...' : 'Upload photo' }}
                        </button>

                        @if (selectedUser.photoUrl) {
                          <button
                            type="button"
                            class="users-page__action"
                            [disabled]="isUserPhotoUploading || isUserPhotoRemoving"
                            (click)="removeSelectedUserPhoto()"
                          >
                            {{ isUserPhotoRemoving ? 'Removing...' : 'Remove photo' }}
                          </button>
                        }
                      </div>
                    </div>
                  }
                </div>
              </div>
            }

            @if (isClinicRoleSelected) {
              <div class="users-page__field">
                <label for="users-clinic-id">{{ clinicLabel }}</label>
                <select id="users-clinic-id" formControlName="clinicId" [disabled]="isViewMode || isSubmitting">
                  <option value="">Select {{ clinicLabelLower }}</option>
                  @for (clinic of clinicOptions; track clinic.clinicId) {
                    <option [value]="clinic.clinicId">{{ clinic.clinicName }}</option>
                  }
                </select>
              </div>
            }

            @if (isSpecialistRoleSelected) {
              <div class="users-page__field users-page__field--wide">
                <label for="users-procedure-type-ids">{{ 'Provided Service Types' | t }}</label>
                @if (procedureTypeOptions.length > 0) {
                  <div
                    id="users-procedure-type-ids"
                    class="users-page__procedure-picker"
                    [class.users-page__procedure-picker--disabled]="!(isCreateMode || isEditMode) || isSubmitting"
                  >
                    @for (procedureType of procedureTypeOptions; track procedureType.id) {
                      <label class="users-page__procedure-option">
                        <input
                          type="checkbox"
                          [checked]="isProcedureTypeSelected(procedureType.id)"
                          [disabled]="!(isCreateMode || isEditMode) || isSubmitting"
                          (change)="onProcedureTypeToggle(procedureType.id, $any($event.target).checked)"
                        />
                        <span>{{ procedureType.name }}</span>
                      </label>
                    }
                  </div>
                  <div class="users-page__account-meta">
                    Selected: {{ selectedProcedureTypeNamesText || 'None' }}
                  </div>
                } @else {
                  <div class="users-page__procedure-empty">No active service types found in company catalog.</div>
                }
              </div>
            }

            @if ((!isCreateMode || isCreateClientRole) && !isSelectedClientEntity) {
              <label class="users-page__checkbox">
                <input type="checkbox" formControlName="isActive" [disabled]="!isCreateMode || isSubmitting" />
                {{ 'Active' | t }}
              </label>
            }

            @if (!isCreateMode && isSelectedClientUser) {
              <div class="users-page__field users-page__field--wide users-page__client-account">
                <label>Account</label>
                @if (isClientAccountLoading) {
                  <div class="users-page__account-meta">Loading...</div>
                } @else {
                  <div class="users-page__account-meta-grid">
                    <div>
                      <div class="users-page__account-meta-label">Email</div>
                      <div class="users-page__account-meta-value">{{ clientAccount?.email ?? '—' }}</div>
                    </div>
                    <div>
                      <div class="users-page__account-meta-label">Account status</div>
                      <div class="users-page__account-meta-value">{{ clientAccountStatusLabel }}</div>
                    </div>
                    <div>
                      <div class="users-page__account-meta-label">Account origin</div>
                      <div class="users-page__account-meta-value">{{ clientAccountOriginLabel }}</div>
                    </div>
                    <div>
                      <div class="users-page__account-meta-label">Created</div>
                      <div class="users-page__account-meta-value">{{ clientAccount?.createdAt ? (clientAccount?.createdAt | date: 'dd.MM.yyyy HH:mm') : '—' }}</div>
                    </div>
                    <div>
                      <div class="users-page__account-meta-label">Updated</div>
                      <div class="users-page__account-meta-value">{{ clientAccount?.updatedAt ? (clientAccount?.updatedAt | date: 'dd.MM.yyyy HH:mm') : '—' }}</div>
                    </div>
                    @if (clientAccount?.accountStatus === 'invited' && clientAccount?.inviteExpiresAt; as clientInviteExpiresAt) {
                      <div>
                        <div class="users-page__account-meta-label">Invite expires at</div>
                        <div class="users-page__account-meta-value">{{ clientInviteExpiresAt | date: 'dd.MM.yyyy HH:mm' }}</div>
                      </div>
                    }
                  </div>
                }

                @if (clientCommunicationSummary; as communicationSummary) {
                  <div class="users-page__communication">
                    <div class="users-page__communication-head">
                      <div>
                        <strong>Communication channels</strong>
                        <div class="users-page__account-meta">{{ communicationSummary.meta }}</div>
                      </div>
                      <span
                        class="users-page__communication-status"
                        [class.users-page__communication-status--automatic]="communicationSummary.tone === 'automatic'"
                        [class.users-page__communication-status--manual]="communicationSummary.tone === 'manual'"
                        [class.users-page__communication-status--unreachable]="communicationSummary.tone === 'unreachable'"
                      >
                        {{ communicationSummary.label }}
                      </span>
                    </div>

                    <div class="users-page__communication-grid">
                      @for (channel of communicationSummary.channels; track channel.id) {
                        <div
                          class="users-page__communication-channel"
                          [class.users-page__communication-channel--automatic]="channel.tone === 'automatic'"
                          [class.users-page__communication-channel--manual]="channel.tone === 'manual'"
                          [class.users-page__communication-channel--unreachable]="channel.tone === 'unreachable'"
                        >
                          <span>{{ channel.title }}</span>
                          <strong>{{ channel.value }}</strong>
                          <small>{{ channel.meta }}</small>
                        </div>
                      }
                    </div>
                  </div>
                }

                @if (isEditMode) {
                  <label>Email</label>
                  <input
                    type="email"
                    [value]="clientEmailDraft"
                    [readonly]="!canEditModal || isClientAccountLoading || isClientEmailSaving"
                    (input)="onClientEmailDraftChange($event)"
                    placeholder="client@example.com"
                  />

                  <div class="users-page__client-actions-row">
                    <button
                      type="button"
                      class="users-page__action"
                      [disabled]="!canEditModal || isClientAccountLoading || isClientEmailSaving || !isClientEmailDraftValid"
                      (click)="saveClientEmail()"
                    >
                      {{ isClientEmailSaving ? 'Saving...' : 'Add/Update email' }}
                    </button>

                    @if (showClientInviteAction) {
                      <button
                        type="button"
                        class="users-page__action users-page__action--primary"
                        [disabled]="!canEditModal || !canSendClientInvite || isClientInviteSending"
                        (click)="sendClientInvite()"
                      >
                        {{ isClientInviteSending ? 'Sending...' : clientInviteActionLabel }}
                      </button>
                    }
                  </div>
                }

                @if (canManageSelectedClientTelegramLink) {
                  <div class="users-page__client-channel">
                    <div>
                      <strong>Telegram</strong>
                      <div class="users-page__account-meta">
                        @if (isClientTelegramLoading) {
                          Loading Telegram link...
                        } @else if (clientTelegramLink?.status === 'active') {
                          Connected{{ clientTelegramLink?.telegramUsername ? ': @' + clientTelegramLink?.telegramUsername : '' }}
                        } @else {
                          Not connected
                        }
                      </div>
                    </div>

                    @if (clientTelegramLinkToken) {
                      <div class="users-page__telegram-link-box">
                        <div>
                          <strong>Connection link</strong>
                          <div class="users-page__account-meta">
                            Expires at {{ clientTelegramLinkToken.expiresAt | date: 'dd.MM.yyyy HH:mm' }}
                          </div>
                        </div>
                        @if (clientTelegramQrDataUrl) {
                          <img
                            [src]="clientTelegramQrDataUrl"
                            alt="Telegram connection QR code"
                            class="users-page__telegram-qr"
                          />
                        }
                        <a [href]="clientTelegramLinkToken.linkUrl" target="_blank" rel="noopener noreferrer">
                          {{ clientTelegramLinkToken.linkUrl }}
                        </a>
                      </div>
                    }

                    <div class="users-page__client-actions-row">
                      <button
                        type="button"
                        class="users-page__action"
                        [disabled]="isClientTelegramLoading || isClientTelegramTokenCreating"
                        (click)="createSelectedClientTelegramLinkToken()"
                      >
                        {{ isClientTelegramTokenCreating ? 'Preparing...' : 'Generate Telegram link' }}
                      </button>

                      @if (clientTelegramLinkToken) {
                        <button
                          type="button"
                          class="users-page__action"
                          [disabled]="isClientTelegramLinkCopying"
                          (click)="copySelectedClientTelegramLink()"
                        >
                          {{ isClientTelegramLinkCopying ? 'Copying...' : 'Copy Telegram link' }}
                        </button>
                      }

                      @if (clientTelegramLink?.status === 'active') {
                        <button
                          type="button"
                          class="users-page__action"
                          [disabled]="isClientTelegramRevoking"
                          (click)="revokeSelectedClientTelegramLink()"
                        >
                          {{ isClientTelegramRevoking ? 'Revoking...' : 'Revoke Telegram' }}
                        </button>
                      }
                    </div>
                  </div>
                }

                @if (clientAccountActionErrorText) {
                  <div class="users-page__inline-error">{{ clientAccountActionErrorText | t }}</div>
                }

                @if (clientAccountSuccessText) {
                  <div class="users-page__inline-success">{{ clientAccountSuccessText }}</div>
                }
              </div>
            }

            @if (!isCreateMode && isSelectedStaffInviteUser) {
              <div class="users-page__field users-page__field--wide users-page__client-account">
                <label>Account Status</label>
                <div class="users-page__client-account-status">
                  {{ staffAccountStatusLabel }}
                </div>
                @if (selectedUser?.accountStatus === 'invited' && selectedUser?.inviteExpiresAt; as staffInviteExpiresAt) {
                  <div class="users-page__account-meta">
                    Invite expires at {{ staffInviteExpiresAt | date: 'dd.MM.yyyy HH:mm' }}
                  </div>
                }

                @if (isEditMode) {
                  <label>Email</label>
                  <input
                    type="email"
                    [value]="staffEmailDraft"
                    [readonly]="!canEditModal || isStaffEmailSaving"
                    (input)="onStaffEmailDraftChange($event)"
                    placeholder="staff@example.com"
                  />

                  <div class="users-page__client-actions-row">
                    <button
                      type="button"
                      class="users-page__action"
                      [disabled]="!canEditModal || isStaffEmailSaving || !isStaffEmailDraftValid"
                      (click)="saveStaffEmail()"
                    >
                      {{ isStaffEmailSaving ? 'Saving...' : 'Add/Update email' }}
                    </button>

                    <button
                      type="button"
                      class="users-page__action users-page__action--primary"
                      [disabled]="!canEditModal || !canSendStaffInvite || isStaffInviteSending"
                      (click)="sendStaffInvite()"
                    >
                      {{ isStaffInviteSending ? 'Sending...' : staffInviteActionLabel }}
                    </button>
                  </div>
                }

                @if (staffAccountActionErrorText) {
                  <div class="users-page__inline-error">{{ staffAccountActionErrorText | t }}</div>
                }

                @if (staffAccountSuccessText) {
                  <div class="users-page__inline-success">{{ staffAccountSuccessText }}</div>
                }
              </div>
            }

            @if (actionErrorText) {
              <div class="users-page__inline-error users-page__field--wide">
                {{ actionErrorText | t }}
              </div>
            }

            @if (successText) {
              <div class="users-page__inline-success users-page__field--wide">
                {{ successText }}
              </div>
            }

            <div class="users-page__modal-actions">
              <button type="button" class="users-page__action" [disabled]="isSubmitting" (click)="closeCreateModal()">
                {{ 'common.cancel' | t }}
              </button>
              @if (!isViewMode) {
                <button type="submit" class="users-page__action users-page__action--primary" [disabled]="!canSubmitModal">
                  {{ modalSubmitLabel | t }}
                </button>
              }
            </div>

            @if (!isCreateMode && isSelectedClientUser) {
              <div class="users-page__field users-page__field--wide users-page__client-reliability">
                <label>{{ 'clientReliability.title' | t }}</label>
                <div
                  class="users-page__client-score-pill"
                  [class.users-page__client-score-pill--good]="selectedClientReliabilityTone === 'good'"
                  [class.users-page__client-score-pill--warn]="selectedClientReliabilityTone === 'warn'"
                  [class.users-page__client-score-pill--risk]="selectedClientReliabilityTone === 'risk'"
                >
                  {{ selectedClientReliabilityPercent }}% · {{ selectedClientReliabilityLabel | t }}
                </div>

                @if (canManualAdjustClientReliability) {
                  <label>{{ 'clientReliability.manualAdjustmentTitle' | t }}</label>
                  <div class="users-page__client-actions">
                    <label class="users-page__client-rating-adjustment">
                      <span>{{ 'clientReliability.manualDelta' | t }}</span>
                      <input
                        type="number"
                        [value]="clientRatingDeltaDraft"
                        [readOnly]="isClientRatingAdjusting"
                        (input)="onClientRatingDeltaChange($event)"
                      />
                    </label>

                    <label class="users-page__client-email users-page__client-email--wide">
                      <span>{{ 'clientReliability.manualReason' | t }}</span>
                      <textarea
                        [value]="clientRatingNoteDraft"
                        [readOnly]="isClientRatingAdjusting"
                        (input)="onClientRatingNoteChange($event)"
                        [placeholder]="clientRatingReasonRequired ? ('clientReliability.manualReasonRequired' | t) : ''"
                      ></textarea>
                    </label>

                    <div class="users-page__client-actions-row">
                      <button
                        type="button"
                        class="users-page__action users-page__action--primary"
                        [disabled]="!canSubmitClientRatingAdjustment"
                        (click)="submitClientRatingAdjustment()"
                      >
                        {{ isClientRatingAdjusting ? ('Saving...' | t) : ('clientReliability.manualSubmit' | t) }}
                      </button>
                    </div>

                    @if (clientRatingAdjustmentErrorText) {
                      <div class="users-page__inline-error">{{ clientRatingAdjustmentErrorText | t }}</div>
                    }

                    @if (clientRatingAdjustmentSuccessText) {
                      <div class="users-page__inline-success">{{ clientRatingAdjustmentSuccessText | t }}</div>
                    }
                  </div>
                }

                <label>{{ 'clientReliability.historyTitle' | t }}</label>
                @if (isClientRatingHistoryLoading) {
                  <div class="users-page__account-meta">{{ 'Loading...' | t }}</div>
                } @else if (clientRatingHistory.length === 0) {
                  <div class="users-page__account-meta">—</div>
                } @else {
                  <div class="users-page__rating-history">
                    @for (event of clientRatingHistory; track event.id) {
                      <div class="users-page__rating-history-item">
                        <div class="users-page__rating-history-top">
                          <strong>{{ clientRatingEventLabel(event) | t }}</strong>
                          <span
                            class="users-page__rating-delta"
                            [class.users-page__rating-delta--positive]="event.deltaPercent > 0"
                            [class.users-page__rating-delta--negative]="event.deltaPercent < 0"
                          >
                            {{ event.deltaPercent > 0 ? '+' : '' }}{{ event.deltaPercent }}%
                          </span>
                        </div>
                        <div class="users-page__account-meta">
                          {{ event.createdAt | date: 'dd.MM.yyyy HH:mm' }} ·
                          {{ event.scoreBefore }}% → {{ event.scoreAfter }}%
                        </div>
                        @if (clientRatingEventMeta(event); as eventMeta) {
                          <div class="users-page__account-meta">
                            {{ 'clientReliability.churnStep' | t }}: {{ eventMeta }}
                          </div>
                        }
                        @if (event.note) {
                          <div class="users-page__account-meta">{{ event.note }}</div>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </form>
        </div>
      </div>
    }

    <lib-page-context-guard [blocked]="companyContextMissing">
      <app-company-context-required />
    </lib-page-context-guard>

    @if (!companyContextMissing) {
      <lib-data-table-shell
        [isLoading]="isLoading"
        [hasError]="!!errorText"
        [isEmpty]="!isLoading && !errorText && roleFilteredItems.length === 0"
      >
        <app-loading-block dataShellLoading text="Loading users..." />

        <app-empty-state
          dataShellError
          [title]="errorText === 'error.settingsCompanyContextRequired' ? 'Company is required' : 'Failed to load users'"
          [description]="errorText ?? ''"
        />

        <app-empty-state
          dataShellEmpty
          [title]="items.length === 0
            ? (isClientTabActive ? 'No clients found' : 'No users found')
            : (isClientTabActive ? 'No clients match filters' : 'No users in selected role')"
          [description]="items.length === 0
            ? (isClientTabActive ? 'No clients available for this company yet.' : 'No users available for this company yet.')
            : 'Try another role tab or adjust scope filters.'"
        />

        <div class="users-page__table-wrap">
          <table class="users-page__table">
            <thead>
              <tr>
                <th class="users-page__col users-page__col--photo">Photo</th>
                <th>{{ 'auth.email' | t }}</th>
                <th>{{ 'Full Name' | t }}</th>
                <th>{{ 'Phone' | t }}</th>
                <th>{{ clinicLabel }}</th>
                <th>{{ 'Rating' | t }}</th>
                <th class="users-page__col users-page__col--role">{{ 'Role' | t }}</th>
                <th>{{ isClientTabActive ? 'Account Status' : ('common.status' | t) }}</th>
                <th class="users-page__col users-page__col--created">{{ 'Created' | t }}</th>
                <th class="users-page__col users-page__col--updated">{{ 'Updated' | t }}</th>
                <th class="users-page__col users-page__col--actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (item of roleFilteredItems; track item.id) {
                <tr
                  class="users-page__table-row"
                  (click)="openUser(item)"
                >
                  <td class="users-page__col users-page__col--photo">
                    @if (item.photoUrl) {
                      <div
                        class="users-page__avatar-preview"
                        #userAvatarSentinel
                        [attr.data-user-id]="item.id"
                        (click)="$event.stopPropagation()"
                        (mouseenter)="setHoveredAvatar(item.id)"
                        (mouseleave)="clearHoveredAvatar(item.id)"
                      >
                        @if (isAvatarVisible(item.id)) {
                        <img
                          [src]="item.photoUrl"
                          alt="User avatar"
                          class="users-page__avatar-icon"
                          loading="lazy"
                          decoding="async"
                          fetchpriority="low"
                        />
                        } @else {
                        <div class="users-page__avatar-icon users-page__avatar-icon--placeholder">
                          {{ userAvatarText(item) }}
                        </div>
                        }
                        @if (hoveredAvatarId === item.id) {
                        <div class="users-page__avatar-popover">
                          <img
                            [src]="item.photoUrl"
                            alt="User avatar preview"
                            class="users-page__avatar-popover-image"
                            decoding="async"
                            fetchpriority="low"
                          />
                        </div>
                        }
                      </div>
                    } @else {
                      <div class="users-page__avatar-icon users-page__avatar-icon--placeholder">
                        {{ userAvatarText(item) }}
                      </div>
                    }
                  </td>
                  <td>{{ item.email || '—' }}</td>
                  <td>{{ item.fullName }}</td>
                  <td>{{ item.phone ?? '—' }}</td>
                  <td>
                    @if (item.clinicId) {
                      <lib-hover-id [value]="item.clinicId">
                        {{ clinicDisplayName(item) }}
                      </lib-hover-id>
                    } @else {
                      —
                    }
                  </td>
                  <td>
                    @if (item.role === 'client') {
                      <span
                        class="users-page__client-score-pill"
                        [class.users-page__client-score-pill--good]="clientReliabilityTone(item) === 'good'"
                        [class.users-page__client-score-pill--warn]="clientReliabilityTone(item) === 'warn'"
                        [class.users-page__client-score-pill--risk]="clientReliabilityTone(item) === 'risk'"
                      >
                        {{ item.clientProfile?.ratingPercent ?? 100 }}% · {{ clientReliabilityLabel(item) | t }}
                      </span>
                    } @else {
                      —
                    }
                  </td>
                  <td class="users-page__col users-page__col--role">{{ ('role.' + item.role) | t }}</td>
                  <td>
                    @if (isClientTabActive) {
                      <span class="users-page__client-status-pill">
                        {{ clientStatusLabel(item) }}
                      </span>
                    } @else {
                      <span class="users-page__status-label">
                        {{ item.isActive ? ('Active' | t) : ('Inactive' | t) }}
                      </span>
                    }
                  </td>
                  <td class="users-page__col users-page__col--created">{{ item.createdAt | date: 'dd.MM.yyyy HH:mm' }}</td>
                  <td class="users-page__col users-page__col--updated">{{ item.updatedAt | date: 'dd.MM.yyyy HH:mm' }}</td>
                  <td class="users-page__col users-page__col--actions" (click)="$event.stopPropagation()">
                    <lib-action-menu [menuTemplate]="tableUserActionsMenu" buttonAriaLabel="Open user actions">
                      <ng-template #tableUserActionsMenu let-close="close">
                        <button
                          type="button"
                          class="action-menu__item"
                          (click)="openUser(item); close()"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          class="action-menu__item action-menu__item--primary"
                          [disabled]="!canEditModal || isSubmitting"
                          (click)="openUserForEdit(item); close()"
                        >
                          Edit
                        </button>
                        @if (item.role === 'client') {
                          <button
                            type="button"
                            class="action-menu__item action-menu__item--danger"
                            [disabled]="isClientDeleting || !!item.linkedUserId"
                            (click)="deleteClientItem(item); close()"
                          >
                            Delete
                          </button>
                        } @else if (item.sourceKind === 'user') {
                          <button
                            type="button"
                            class="action-menu__item"
                            [class.action-menu__item--danger]="item.isActive"
                            [class.action-menu__item--primary]="!item.isActive"
                            (click)="toggleUserActive($event, item); close()"
                          >
                            {{ item.isActive ? 'Deactivate' : 'Activate' }}
                          </button>
                        }
                      </ng-template>
                    </lib-action-menu>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <div class="users-page__cards">
          @for (item of roleFilteredItems; track item.id) {
            <article
              class="users-page__card"
              (click)="openUser(item)"
            >
              <div class="users-page__card-head">
                <div class="users-page__card-identity">
                  <div
                    #userAvatarSentinel
                    [attr.data-user-id]="item.id"
                    class="users-page__card-avatar-sentinel"
                  >
                  @if (item.photoUrl && isAvatarVisible(item.id)) {
                    <img
                      [src]="item.photoUrl"
                      alt="User avatar"
                      class="users-page__card-avatar"
                      loading="lazy"
                      decoding="async"
                      fetchpriority="low"
                    />
                  } @else if (item.photoUrl) {
                    <div class="users-page__card-avatar users-page__card-avatar--placeholder">
                      {{ userAvatarText(item) }}
                    </div>
                  } @else {
                    <div class="users-page__card-avatar users-page__card-avatar--placeholder">
                      {{ userAvatarText(item) }}
                    </div>
                  }
                  </div>

                  <div class="users-page__card-titles">
                    <div class="users-page__card-title">{{ item.fullName || '—' }}</div>
                    <div class="users-page__card-subtitle">{{ item.email || '—' }}</div>
                  </div>
                </div>

                @if (isClientTabActive) {
                  <span class="users-page__client-status-pill">
                    {{ clientStatusLabel(item) }}
                  </span>
                } @else {
                  <span class="users-page__card-role">{{ ('role.' + item.role) | t }}</span>
                }
              </div>

              <div class="users-page__card-grid">
                <div class="users-page__card-row">
                  <div class="users-page__card-label">{{ 'Phone' | t }}</div>
                  <div class="users-page__card-value">{{ item.phone ?? '—' }}</div>
                </div>

                <div class="users-page__card-row">
                  <div class="users-page__card-label">{{ clinicLabel }}</div>
                  <div class="users-page__card-value">
                    @if (item.clinicId) {
                      <lib-hover-id [value]="item.clinicId">
                        {{ clinicDisplayName(item) }}
                      </lib-hover-id>
                    } @else {
                      —
                    }
                  </div>
                </div>

                @if (item.role === 'client') {
                  <div class="users-page__card-row">
                    <div class="users-page__card-label">{{ 'clientReliability.titleShort' | t }}</div>
                    <div class="users-page__card-value">
                      <span
                        class="users-page__client-score-pill"
                        [class.users-page__client-score-pill--good]="clientReliabilityTone(item) === 'good'"
                        [class.users-page__client-score-pill--warn]="clientReliabilityTone(item) === 'warn'"
                        [class.users-page__client-score-pill--risk]="clientReliabilityTone(item) === 'risk'"
                      >
                        {{ item.clientProfile?.ratingPercent ?? 100 }}% · {{ clientReliabilityLabel(item) | t }}
                      </span>
                    </div>
                  </div>
                }

                <div class="users-page__card-row">
                  <div class="users-page__card-label">
                    {{ isClientTabActive ? 'Account Status' : ('common.status' | t) }}
                  </div>
                  <div class="users-page__card-value">
                    @if (isClientTabActive) {
                      {{ clientStatusLabel(item) }}
                    } @else {
                      {{ item.isActive ? ('Active' | t) : ('Inactive' | t) }}
                    }
                  </div>
                </div>

                <div class="users-page__card-row">
                  <div class="users-page__card-label">{{ 'Created' | t }}</div>
                  <div class="users-page__card-value">{{ item.createdAt | date: 'dd.MM.yyyy HH:mm' }}</div>
                </div>

                <div class="users-page__card-row">
                  <div class="users-page__card-label">{{ 'Updated' | t }}</div>
                  <div class="users-page__card-value">{{ item.updatedAt | date: 'dd.MM.yyyy HH:mm' }}</div>
                </div>
              </div>

              <div class="users-page__card-actions" (click)="$event.stopPropagation()">
                <lib-action-menu [menuTemplate]="cardUserActionsMenu" buttonAriaLabel="Open user actions">
                  <ng-template #cardUserActionsMenu let-close="close">
                    <button
                      type="button"
                      class="action-menu__item"
                      (click)="openUser(item); close()"
                    >
                      View
                    </button>
                    <button
                      type="button"
                      class="action-menu__item action-menu__item--primary"
                      [disabled]="!canEditModal || isSubmitting"
                      (click)="openUserForEdit(item); close()"
                    >
                      Edit
                    </button>
                    @if (item.role === 'client') {
                      <button
                        type="button"
                        class="action-menu__item action-menu__item--danger"
                        [disabled]="isClientDeleting || !!item.linkedUserId"
                        (click)="deleteClientItem(item); close()"
                      >
                        Delete
                      </button>
                    } @else if (item.sourceKind === 'user') {
                      <button
                        type="button"
                        class="action-menu__item"
                        [class.action-menu__item--danger]="item.isActive"
                        [class.action-menu__item--primary]="!item.isActive"
                        (click)="toggleUserActive($event, item); close()"
                      >
                        {{ item.isActive ? 'Deactivate' : 'Activate' }}
                      </button>
                    }
                  </ng-template>
                </lib-action-menu>
              </div>

            </article>
          }
        </div>

        @if (!isLoading && !errorText && pageHasMore) {
          <div class="users-page__load-more">
            <button
              type="button"
              class="users-page__action users-page__action--primary"
              [disabled]="isClientPageLoadingMore"
              (click)="loadMoreClientItems()"
            >
              {{ isClientPageLoadingMore ? 'Loading more...' : 'Load more' }}
            </button>
          </div>
        }
      </lib-data-table-shell>
    }
    </lib-page-shell>
  `,
  styles: [`
    .users-page__scope-form {
      margin-bottom: 16px;
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 16px;
    }

    .users-page__scope-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
      align-items: end;
    }

    .users-page__grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 12px;
      align-items: end;
    }

    .users-page__field {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .users-page__field--wide {
      grid-column: span 2;
    }

    .users-page__field label {
      font-size: 13px;
      font-weight: 600;
      color: #374151;
    }

    .users-page__procedure-picker {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 10px;
      padding: 14px;
      border: 1px solid #dbe2ea;
      border-radius: 14px;
      background: #f8fafc;
    }

    .users-page__procedure-picker--disabled {
      opacity: 0.7;
    }

    .users-page__procedure-option {
      display: flex;
      align-items: center;
      gap: 10px;
      min-height: 44px;
      padding: 10px 12px;
      border: 1px solid #d7dee8;
      border-radius: 12px;
      background: #ffffff;
      color: #111827;
      cursor: pointer;
    }

    .users-page__procedure-option input {
      width: 16px;
      height: 16px;
      margin: 0;
      flex: 0 0 auto;
    }

    .users-page__procedure-option span {
      min-width: 0;
      font-size: 14px;
      line-height: 1.35;
    }

    .users-page__procedure-empty {
      padding: 12px 14px;
      border: 1px dashed #d1d5db;
      border-radius: 12px;
      background: #f8fafc;
      color: #6b7280;
      font-size: 13px;
    }

    .users-page__client-account {
      display: grid;
      grid-template-columns: 1fr;
      gap: 8px;
      padding-top: 4px;
      border-top: 1px solid #f3f4f6;
    }

    .users-page__client-channel {
      display: grid;
      gap: 10px;
      padding-top: 12px;
      border-top: 1px solid #e5e7eb;
    }

    .users-page__communication {
      display: grid;
      gap: 10px;
      padding: 12px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: #f8fafc;
    }

    .users-page__communication-head {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: flex-start;
    }

    .users-page__communication-status {
      flex: 0 0 auto;
      padding: 4px 8px;
      border-radius: 999px;
      border: 1px solid #d1d5db;
      background: #fff;
      font-size: 12px;
      font-weight: 700;
      color: #374151;
    }

    .users-page__communication-status--automatic {
      border-color: #bbf7d0;
      background: #f0fdf4;
      color: #166534;
    }

    .users-page__communication-status--manual {
      border-color: #fde68a;
      background: #fffbeb;
      color: #92400e;
    }

    .users-page__communication-status--unreachable {
      border-color: #fecaca;
      background: #fef2f2;
      color: #991b1b;
    }

    .users-page__communication-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 8px;
    }

    .users-page__communication-channel {
      display: grid;
      gap: 4px;
      min-width: 0;
      padding: 10px;
      border: 1px solid #e5e7eb;
      border-left-width: 3px;
      border-radius: 8px;
      background: #fff;
    }

    .users-page__communication-channel--automatic {
      border-left-color: #22c55e;
    }

    .users-page__communication-channel--manual {
      border-left-color: #f59e0b;
    }

    .users-page__communication-channel--unreachable {
      border-left-color: #ef4444;
    }

    .users-page__communication-channel span {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      color: #6b7280;
    }

    .users-page__communication-channel strong,
    .users-page__communication-channel small {
      overflow-wrap: anywhere;
    }

    .users-page__communication-channel small {
      color: #6b7280;
      line-height: 1.3;
    }

    .users-page__telegram-link-box {
      display: grid;
      gap: 8px;
      padding: 12px;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
      background: #eff6ff;
    }

    .users-page__telegram-link-box a {
      overflow-wrap: anywhere;
    }

    .users-page__telegram-qr {
      width: 180px;
      height: 180px;
      border: 1px solid #dbeafe;
      border-radius: 8px;
      background: #fff;
      padding: 8px;
    }

    @media (max-width: 760px) {
      .users-page__communication-head {
        display: grid;
      }

      .users-page__communication-grid {
        grid-template-columns: 1fr;
      }
    }

    .users-page__client-reliability {
      display: grid;
      grid-template-columns: 1fr;
      gap: 10px;
      padding-top: 4px;
      border-top: 1px solid #f3f4f6;
    }

    .users-page__photo-block {
      display: grid;
      gap: 12px;
      justify-items: start;
    }

    .users-page__photo-image,
    .users-page__photo-placeholder {
      width: 112px;
      height: 112px;
      border-radius: 16px;
      border: 1px solid #dbe2ea;
      background: #f8fafc;
    }

    .users-page__photo-image {
      object-fit: cover;
    }

    .users-page__photo-placeholder {
      display: grid;
      place-items: center;
      padding: 12px;
      color: #6b7280;
      font-size: 12px;
      text-align: center;
    }

    .users-page__photo-controls {
      display: grid;
      gap: 8px;
      justify-items: start;
    }

    .users-page__avatar-preview {
      position: relative;
      width: 28px;
      height: 28px;
    }

    .users-page__avatar-icon {
      width: 28px;
      height: 28px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 999px;
      border: 1px solid #cbd5e1;
      background: #f8fafc;
      cursor: zoom-in;
      color: #475569;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
    }

    .users-page__avatar-icon--placeholder {
      color: #475569;
    }

    .users-page__avatar-popover {
      position: absolute;
      left: calc(100% + 10px);
      top: 50%;
      transform: translateY(-50%);
      width: 144px;
      height: 144px;
      border-radius: 18px;
      border: 1px solid #dbe2ea;
      background: #ffffff;
      box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
      padding: 8px;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.16s ease;
      z-index: 5;
    }

    .users-page__avatar-preview:hover .users-page__avatar-popover {
      opacity: 1;
    }

    .users-page__avatar-popover-image {
      width: 100%;
      height: 100%;
      display: block;
      border-radius: 12px;
      object-fit: cover;
      background: #f8fafc;
    }

    .users-page__avatar-empty {
      width: 28px;
      text-align: center;
      color: #9ca3af;
    }

    .users-page__client-account-status {
      height: 40px;
      display: flex;
      align-items: center;
      padding: 0 10px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: #f9fafb;
      color: #111827;
      font-size: 14px;
      font-weight: 600;
    }

    .users-page__account-meta {
      margin-top: -4px;
      color: #6b7280;
      font-size: 12px;
    }

    .users-page__account-meta-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px 12px;
      padding: 10px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      background: #f8fafc;
    }

    .users-page__account-meta-label {
      color: #6b7280;
      font-size: 12px;
      font-weight: 600;
      margin-bottom: 4px;
    }

    .users-page__account-meta-value {
      color: #111827;
      font-size: 14px;
      word-break: break-word;
    }

    .users-page__rating-history {
      display: grid;
      gap: 8px;
      width: 100%;
    }

    .users-page__rating-history-item {
      display: grid;
      gap: 4px;
      padding: 10px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      background: #f8fafc;
    }

    .users-page__rating-history-top {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: center;
    }

    .users-page__rating-delta {
      font-size: 12px;
      font-weight: 700;
      color: #475569;
      white-space: nowrap;
    }

    .users-page__rating-delta--positive {
      color: #166534;
    }

    .users-page__rating-delta--negative {
      color: #b91c1c;
    }

    .users-page__client-actions-row {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    .users-page__client-rating-adjustment {
      display: grid;
      gap: 6px;
      max-width: 220px;
      font-size: 13px;
      color: #374151;
      font-weight: 600;
    }

    .users-page__inline-error {
      padding: 10px 12px;
      border: 1px solid #fecaca;
      border-radius: 8px;
      background: #fef2f2;
      color: #b91c1c;
      font-size: 13px;
    }

    .users-page__inline-success {
      padding: 10px 12px;
      border: 1px solid #bbf7d0;
      border-radius: 8px;
      background: #f0fdf4;
      color: #166534;
      font-size: 13px;
    }

    .users-page__field input,
    .users-page__field select {
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 10px;
      box-sizing: border-box;
    }

    .users-page__field select[multiple] {
      height: 120px;
      padding: 6px 10px;
    }

    .users-page__checkbox {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: #111827;
      height: 40px;
    }

    .users-page__submit {
      height: 38px;
      border: none;
      border-radius: 8px;
      background: #2563eb;
      color: #ffffff;
      padding: 0 14px;
      cursor: pointer;
    }

    .users-page__submit:disabled {
      opacity: 0.6;
      cursor: default;
    }

    .users-page__role-actions {
      margin: 12px 0;
      display: flex;
      justify-content: flex-end;
    }

    .users-page__filters {
      margin: 0 0 12px;
      padding: 12px;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      background: #ffffff;
    }

    .users-page__filters-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 12px;
      align-items: end;
    }

    .users-page__modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(17, 24, 39, 0.45);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      z-index: 1000;
    }

    .users-page__modal {
      width: min(920px, 100%);
      max-height: calc(100dvh - 32px);
      overflow: auto;
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 16px;
      box-sizing: border-box;
    }

    .users-page__modal-title {
      margin: 0 0 12px;
      color: #111827;
      font-size: 18px;
      font-weight: 700;
    }

    .users-page__modal-actions {
      grid-column: 1 / -1;
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 4px;
    }

    .users-page__table-wrap {
      overflow: auto;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      background: #ffffff;
    }

    .users-page__cards {
      display: none;
    }

    .users-page__table {
      width: 100%;
      border-collapse: collapse;
      min-width: 960px;
    }

    .users-page__table th,
    .users-page__table td {
      text-align: left;
      padding: 12px;
      border-bottom: 1px solid #f3f4f6;
      font-size: 14px;
      color: #111827;
      white-space: nowrap;
    }

    .users-page__table th {
      font-size: 12px;
      text-transform: uppercase;
      color: #6b7280;
      letter-spacing: 0.04em;
    }

    .users-page__table-row {
      cursor: pointer;
    }

    .users-page__table-row:hover {
      background: #f8fafc;
    }

    .users-page__action {
      height: 32px;
      border-radius: 8px;
      border: 1px solid #2563eb;
      background: #ffffff;
      color: #2563eb;
      padding: 0 10px;
      font-size: 13px;
      cursor: pointer;
    }

    .users-page__action--primary {
      background: #2563eb;
      color: #ffffff;
    }

    .users-page__action--danger {
      border-color: #dc2626;
      color: #dc2626;
      background: #ffffff;
    }

    .users-page__status-switch-wrap {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 130px;
      font-size: 12px;
    }

    .users-page__client-status-pill {
      display: inline-flex;
      align-items: center;
      min-height: 32px;
      padding: 0 10px;
      border-radius: 999px;
      border: 1px solid #dbe2ea;
      background: #f8fafc;
      color: #1f2937;
      font-size: 12px;
      font-weight: 700;
    }

    .users-page__client-score-pill {
      display: inline-flex;
      align-items: center;
      min-height: 32px;
      padding: 0 10px;
      border-radius: 999px;
      border: 1px solid #dbe2ea;
      background: #f8fafc;
      color: #334155;
      font-size: 12px;
      font-weight: 700;
      white-space: nowrap;
    }

    .users-page__client-score-pill--good {
      border-color: #bbf7d0;
      background: #f0fdf4;
      color: #166534;
    }

    .users-page__client-score-pill--warn {
      border-color: #fde68a;
      background: #fffbeb;
      color: #92400e;
    }

    .users-page__client-score-pill--risk {
      border-color: #fecaca;
      background: #fef2f2;
      color: #b91c1c;
    }

    .users-page__switch {
      position: relative;
      width: 42px;
      height: 22px;
      border: none;
      border-radius: 999px;
      cursor: pointer;
      transition: background-color 0.2s ease;
      padding: 0;
    }

    .users-page__switch--on {
      background: #22c55e;
    }

    .users-page__switch--off {
      background: #d1d5db;
    }

    .users-page__switch-thumb {
      position: absolute;
      top: 2px;
      left: 2px;
      width: 18px;
      height: 18px;
      border-radius: 999px;
      background: #ffffff;
      transition: transform 0.2s ease;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    }

    .users-page__switch--on .users-page__switch-thumb {
      transform: translateX(20px);
    }

    .users-page__status-label {
      color: #374151;
      font-size: 12px;
      font-weight: 600;
    }

    .users-page__card {
      border: 1px solid #e5e7eb;
      border-radius: 16px;
      background: #ffffff;
      padding: 14px;
      display: grid;
      gap: 12px;
      cursor: pointer;
    }

    .users-page__card-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 10px;
    }

    .users-page__card-identity {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
    }

    .users-page__card-avatar-sentinel {
      flex: 0 0 auto;
      width: 44px;
      height: 44px;
      display: inline-flex;
    }

    .users-page__card-avatar {
      width: 44px;
      height: 44px;
      border-radius: 14px;
      border: 1px solid #dbe2ea;
      background: #f8fafc;
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #475569;
      font-size: 14px;
      font-weight: 700;
      text-transform: uppercase;
    }

    .users-page__card-avatar--empty {
      display: grid;
      place-items: center;
      color: #9ca3af;
      font-size: 18px;
    }

    .users-page__card-avatar--placeholder {
      color: #475569;
    }

    .users-page__card-titles {
      min-width: 0;
      display: grid;
      gap: 2px;
    }

    .users-page__card-title,
    .users-page__card-subtitle,
    .users-page__card-value {
      min-width: 0;
      overflow-wrap: anywhere;
    }

    .users-page__card-title {
      color: #111827;
      font-size: 15px;
      font-weight: 700;
    }

    .users-page__card-subtitle {
      color: #6b7280;
      font-size: 12px;
    }

    .users-page__card-role {
      display: inline-flex;
      align-items: center;
      min-height: 28px;
      padding: 0 10px;
      border-radius: 999px;
      border: 1px solid #dbe2ea;
      background: #f8fafc;
      color: #334155;
      font-size: 12px;
      font-weight: 700;
      white-space: nowrap;
    }

    .users-page__card-grid {
      display: grid;
      gap: 10px;
    }

    .users-page__card-row {
      display: grid;
      grid-template-columns: minmax(0, 108px) minmax(0, 1fr);
      gap: 10px;
      align-items: start;
    }

    .users-page__card-label {
      color: #6b7280;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .users-page__card-value {
      color: #111827;
      font-size: 13px;
      line-height: 1.4;
    }

    .users-page__card-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
      padding-top: 6px;
      border-top: 1px solid #f3f4f6;
    }

    @media (max-width: 900px) {
      .users-page__filters-grid,
      .users-page__grid {
        grid-template-columns: 1fr;
      }

      .users-page__role-actions {
        justify-content: stretch;
      }

      .users-page__submit {
        width: 100%;
      }

      .users-page__modal {
        max-height: calc(100dvh - 32px);
        overflow: auto;
        width: min(100%, 720px);
        padding: 14px;
      }

      .users-page__modal-actions {
        flex-direction: column-reverse;
      }

      .users-page__modal-actions .users-page__action {
        width: 100%;
      }

      .users-page__table-wrap {
        display: none;
      }

      .users-page__cards {
        display: grid;
        gap: 12px;
      }

      .users-page__col--updated {
        display: none;
      }
    }

    @media (max-width: 640px) {
      .users-page__role-actions {
        margin: 10px 0 14px;
      }

      .users-page__filters {
        padding: 10px;
      }

      .users-page__modal-backdrop {
        padding: 8px;
        align-items: end;
      }

      .users-page__modal {
        width: 100%;
        max-height: min(92dvh, calc(100dvh - 12px));
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        padding: 12px;
      }

      .users-page__table th,
      .users-page__table td {
        padding: 10px;
        font-size: 13px;
      }

      .users-page__col--photo,
      .users-page__col--role {
        display: none;
      }

      .users-page__client-status-pill {
        min-height: 28px;
        padding: 0 8px;
      }

      .users-page__card-head {
        align-items: flex-start;
      }

      .users-page__card-row {
        grid-template-columns: 1fr;
        gap: 4px;
      }

      .users-page__card-role {
        white-space: normal;
        text-align: center;
      }

      .users-page__rating-history-top {
        flex-direction: column;
        align-items: flex-start;
      }

      .users-page__account-meta-grid {
        grid-template-columns: 1fr;
      }

      .users-page__client-rating-adjustment {
        max-width: 100%;
      }
    }

  `]
})
export class UsersPageComponent implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChildren('userAvatarSentinel')
  private avatarSentinels?: QueryList<ElementRef<HTMLElement>>;

  private static readonly DEFAULT_CLINIC_LABEL = 'Clinic';
  private static readonly EMAIL_REGEXP = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  private static readonly DEFAULT_ROLE_TAB: StaffRole | 'company_admin' = 'company_admin';
  private static readonly SYSTEM_ADMIN_ROLE_TABS: Array<StaffRole | 'company_admin'> = [
    'company_admin',
    'manager',
    'technician',
    'reception',
    'specialist',
    'client',
  ];

  private readonly usersService = inject(UsersService);
  private readonly companiesService = inject(CompaniesService);
  private readonly clientsAdminService = inject(ClientsAdminService);
  private readonly procedureTypesService = inject(ProcedureTypesService);
  private readonly settingsService = inject(SettingsService);
  private readonly telegramConnectorService = inject(TelegramConnectorService);
  private readonly authService = inject(AuthService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly route = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly subscriptions = new Subscription();

  private readonly creatableRolesByActor: Record<UserRole, Array<StaffRole | 'company_admin'>> = {
    system_admin: ['company_admin', 'manager', 'technician', 'reception', 'specialist', 'client'],
    company_admin: ['manager', 'technician', 'reception', 'specialist', 'client'],
    manager: ['technician', 'reception', 'specialist', 'client'],
    technician: [],
    specialist: [],
    reception: [],
    client: [],
  };

  protected get creatableRoles(): Array<StaffRole | 'company_admin'> {
    const role = (this.authService.getCurrentRole() as UserRole | null) ?? 'client';
    return this.creatableRolesByActor[role] ?? this.creatableRolesByActor.client;
  }

  protected readonly form = this.formBuilder.group({
    companyId: this.formBuilder.nonNullable.control(''),
    email: this.formBuilder.nonNullable.control('', [
      Validators.required,
      Validators.email
    ]),
    phone: this.formBuilder.nonNullable.control(''),
    password: this.formBuilder.nonNullable.control('', [
      Validators.minLength(8)
    ]),
    role: this.formBuilder.nonNullable.control<StaffRole | 'company_admin'>('technician'),
    clinicId: this.formBuilder.nonNullable.control(''),
    procedureTypeIds: this.formBuilder.nonNullable.control<string[]>([]),
    isActive: this.formBuilder.nonNullable.control<boolean>(true),
    fullName: this.formBuilder.nonNullable.control('', [Validators.required, Validators.maxLength(255)]),
    birthDate: this.formBuilder.nonNullable.control(''),
    notes: this.formBuilder.nonNullable.control('', [Validators.maxLength(5000)]),
  });

  protected readonly filtersForm = this.formBuilder.group({
    query: this.formBuilder.nonNullable.control(''),
    userStatus: this.formBuilder.nonNullable.control<UsersStatusFilter>(''),
    clientAccountStatus: this.formBuilder.nonNullable.control<ClientAccountStatusFilter>(''),
    clientEmailPresence: this.formBuilder.nonNullable.control<ClientEmailPresenceFilter>(''),
    clientOrigin: this.formBuilder.nonNullable.control<ClientOriginFilter>(''),
    sort: this.formBuilder.nonNullable.control<UsersSort>('createdAtDesc'),
  });

  protected items: UserItem[] = [];
  protected activeRoleTab: StaffRole | 'company_admin' = UsersPageComponent.DEFAULT_ROLE_TAB;
  protected roleTabs: TabStripItem[] = [];
  protected isCreateModalOpen = false;
  protected isFiltersModalOpen = false;
  protected isLoading = true;
  protected isSubmitting = false;
  protected modalMode: 'create' | 'view' | 'edit' = 'create';
  protected selectedUser: UserItem | null = null;
  protected errorText: string | null = null;
  protected actionErrorText: string | null = null;
  protected successText: string | null = null;
  protected companies: CompanyItem[] = [];
  protected clinicOptions: ClinicOperationalSettingsDto[] = [];
  protected procedureTypeOptions: ProcedureTypeItem[] = [];
  protected clinicLabel = UsersPageComponent.DEFAULT_CLINIC_LABEL;
  protected clientAccount: ClientAdminItem | null = null;
  protected ratingSettings: CompanyRatingSettingsDto | null = null;
  protected clientRatingHistory: UserClientRatingEvent[] = [];
  protected isClientAccountLoading = false;
  protected isClientRatingHistoryLoading = false;
  protected isClientRatingAdjusting = false;
  protected isClientEmailSaving = false;
  protected isClientInviteSending = false;
  protected isClientTelegramLoading = false;
  protected isClientTelegramTokenCreating = false;
  protected isClientTelegramLinkCopying = false;
  protected isClientTelegramRevoking = false;
  protected isClientDeleting = false;
  protected clientEmailDraft = '';
  protected clientTelegramLink: TelegramLinkDto | null = null;
  protected clientTelegramLinkToken: TelegramLinkTokenDto | null = null;
  protected clientTelegramQrDataUrl: string | null = null;
  protected clientAccountActionErrorText: string | null = null;
  protected clientAccountSuccessText: string | null = null;
  protected clientRatingAdjustmentErrorText: string | null = null;
  protected clientRatingAdjustmentSuccessText: string | null = null;
  protected clientRatingDeltaDraft = 0;
  protected clientRatingNoteDraft = '';
  protected isStaffEmailSaving = false;
  protected isStaffInviteSending = false;
  protected staffEmailDraft = '';
  protected staffAccountActionErrorText: string | null = null;
  protected staffAccountSuccessText: string | null = null;
  protected selectedUserPhotoFile: File | null = null;
  protected selectedUserPhotoName = '';
  protected isUserPhotoUploading = false;
  protected isUserPhotoRemoving = false;
  protected appliedFilters: UserManagementFilterState = this.getDefaultFilters();
  protected isClientPageLoadingMore = false;
  protected pageHasMore = false;
  private pageCursor: string | null = null;
  private pendingFocusClientId: string | null = null;
  private handledFocusClientId: string | null = null;
  protected hoveredAvatarId: string | null = null;
  private readonly visibleAvatarIds = new Set<string>();
  private avatarVisibilityObserver: IntersectionObserver | null = null;
  private readonly clinicsCache = new Map<string, {
    clinicLabel: string;
    items: ClinicOperationalSettingsDto[];
  }>();
  private readonly procedureTypesCache = new Map<string, ProcedureTypeItem[]>();
  private readonly ratingSettingsCache = new Map<string, CompanyRatingSettingsDto | null>();
  private readonly loadingClinicKeys = new Set<string>();
  private readonly loadingProcedureTypeKeys = new Set<string>();
  private readonly loadingRatingSettingsKeys = new Set<string>();

  protected get clinicLabelLower(): string {
    return this.lowerFirst(this.clinicLabel);
  }

  protected get canCreateForActiveRole(): boolean {
    return this.creatableRoles.includes(this.activeRoleTab);
  }

  protected get isCreateMode(): boolean {
    return this.modalMode === 'create';
  }

  protected get isViewMode(): boolean {
    return this.modalMode === 'view';
  }

  protected get isEditMode(): boolean {
    return this.modalMode === 'edit';
  }

  protected get canEditModal(): boolean {
    return this.isAllowedRole(this.authService.getCurrentRole() as UserRole | null);
  }

  protected get modalTitle(): string {
    if (this.isCreateMode) {
      return this.isCreateClientRole ? 'users.createClient' : 'Create user';
    }
    if (this.isEditMode) {
      return 'users.editUser';
    }
    if (this.isSelectedClientUser) {
      return 'users.clientQuickView';
    }
    return 'users.userProfile';
  }

  protected get modalSubmitLabel(): string {
    if (this.isSubmitting) {
      return this.isCreateMode ? 'common.creating' : 'common.saving';
    }
    if (!this.isCreateMode) {
      return 'common.save';
    }
    return this.isCreateClientRole ? 'users.createClient' : 'Create user';
  }

  protected get canSubmitModal(): boolean {
    if (this.isViewMode || this.isSubmitting || this.form.invalid) {
      return false;
    }
    if (this.isCreateMode && this.isCreateClientRole && !this.hasCreateClientContactChannel()) {
      return false;
    }
    if (this.isEditMode && !this.selectedUser) {
      return false;
    }
    return true;
  }

  protected get createButtonText(): string {
    if (this.activeRoleTab === 'client') {
      return 'users.createClient';
    }

    return `Create ${this.roleTabLabel(this.activeRoleTab)}`;
  }

  protected get isCreateClientRole(): boolean {
    return this.form.controls.role.value === 'client';
  }

  protected get isCreateClientContactMissing(): boolean {
    return this.isCreateMode
      && this.isCreateClientRole
      && !this.hasCreateClientContactChannel();
  }

  private hasCreateClientContactChannel(): boolean {
    if (!this.isCreateMode || !this.isCreateClientRole) {
      return true;
    }

    return !!this.form.controls.email.value.trim()
      || !!this.form.controls.phone.value.trim();
  }

  protected get roleFilteredItems(): UserItem[] {
    return this.items.filter((item) => item.role === this.activeRoleTab);
  }

  protected get hasActiveFilters(): boolean {
    const defaults = this.getDefaultFilters();
    return this.appliedFilters.query !== defaults.query
      || this.appliedFilters.userStatus !== defaults.userStatus
      || this.appliedFilters.clientAccountStatus !== defaults.clientAccountStatus
      || this.appliedFilters.clientEmailPresence !== defaults.clientEmailPresence
      || this.appliedFilters.clientOrigin !== defaults.clientOrigin
      || this.appliedFilters.sort !== defaults.sort;
  }

  protected get isSystemAdmin(): boolean
  {
    return (this.authService.getCurrentRole() as UserRole | null) === 'system_admin';
  }

  protected get isReceptionRoleSelected(): boolean
  {
    return this.form.controls.role.value === 'reception';
  }

  protected get isSpecialistRoleSelected(): boolean
  {
    return this.form.controls.role.value === 'specialist';
  }

  protected get isClinicRoleSelected(): boolean
  {
    const role = this.form.controls.role.value;
    return role === 'reception' || role === 'specialist';
  }

  protected get isSelectedClientUser(): boolean {
    return this.selectedUser?.role === 'client';
  }

  protected get isSelectedClientEntity(): boolean {
    return this.selectedUser?.role === 'client';
  }

  protected get canShowSelectedUserPhoto(): boolean {
    return !!this.selectedUser?.linkedUserId;
  }

  protected get isClientTabActive(): boolean {
    return this.activeRoleTab === 'client';
  }

  protected get isSelectedStaffInviteUser(): boolean {
    const role = this.selectedUser?.role ?? null;
    return role !== null
      && role !== 'client'
      && role !== 'system_admin';
  }

  protected get clientAccountStatusLabel(): string {
    if (!this.clientAccount) {
      return 'No account';
    }

    if (this.clientAccount.accountStatus === 'registered') {
      return 'Registered';
    }

    if (this.clientAccount.accountStatus === 'invited') {
      return 'Invited';
    }

    return 'No account';
  }

  protected get isClientEmailDraftValid(): boolean {
    const value = this.clientEmailDraft.trim().toLowerCase();
    return UsersPageComponent.EMAIL_REGEXP.test(value);
  }

  protected get canSendClientInvite(): boolean {
    if (!this.clientAccount || this.isClientAccountLoading) {
      return false;
    }

    if (this.clientAccount.accountStatus === 'registered') {
      return false;
    }

    const email = this.clientAccount.email?.trim().toLowerCase() ?? '';
    return UsersPageComponent.EMAIL_REGEXP.test(email);
  }

  protected get clientInviteActionLabel(): string {
    if (this.clientAccount?.accountStatus === 'invited') {
      return 'Resend invite';
    }

    return 'Send invite';
  }

  protected get clientAccountOriginLabel(): string {
    switch (this.clientAccount?.accountOrigin) {
      case 'invite':
        return 'Invite';
      case 'self_registration':
        return 'Self registration';
      case 'offline_only':
        return 'Offline only';
      default:
        return '—';
    }
  }

  protected get clientCommunicationSummary(): ClientCommunicationSummaryView | null {
    if (!this.clientAccount) {
      return null;
    }

    return presentClientCommunicationChannels(this.clientAccount.communicationChannels);
  }

  protected get showClientInviteAction(): boolean {
    return this.clientAccount?.accountStatus !== 'registered';
  }

  protected get canManageSelectedClientTelegramLink(): boolean {
    if (this.isCreateMode || !this.selectedUser || this.selectedUser.role !== 'client' || !this.clientAccount) {
      return false;
    }

    const role = this.authService.getCurrentRole() as UserRole | null;
    return role === 'system_admin' ||
      role === 'company_admin' ||
      role === 'manager' ||
      role === 'reception';
  }

  protected get selectedClientReliabilityPercent(): number {
    if (this.clientAccount) {
      return this.clientAccount.ratingPercent;
    }

    return this.selectedUser?.clientProfile?.ratingPercent ?? 100;
  }

  protected get selectedClientReliabilityLabel(): string {
    if (this.selectedClientReliabilityPercent >= 85) {
      return 'clientReliability.label.reliable';
    }

    if (this.selectedClientReliabilityPercent >= 60) {
      return 'clientReliability.label.watch';
    }

    return 'clientReliability.label.risk';
  }

  protected get selectedClientReliabilityTone(): 'good' | 'warn' | 'risk' {
    if (this.selectedClientReliabilityPercent >= 85) {
      return 'good';
    }

    if (this.selectedClientReliabilityPercent >= 60) {
      return 'warn';
    }

    return 'risk';
  }

  protected get clientRatingReasonRequired(): boolean {
    return !!this.ratingSettings?.manualAdjustmentAllowed
      && !!this.ratingSettings?.manualAdjustmentReasonRequired;
  }

  protected get canManualAdjustClientReliability(): boolean {
    return !!this.selectedUser
      && this.selectedUser.role === 'client'
      && !!this.canEditModal
      && !!this.ratingSettings?.manualAdjustmentAllowed;
  }

  protected get canSubmitClientRatingAdjustment(): boolean {
    if (!this.canManualAdjustClientReliability || this.isClientRatingAdjusting) {
      return false;
    }

    if (!Number.isFinite(this.clientRatingDeltaDraft) || this.clientRatingDeltaDraft === 0) {
      return false;
    }

    if (this.clientRatingReasonRequired && !this.clientRatingNoteDraft.trim()) {
      return false;
    }

    return true;
  }

  protected get staffAccountStatusLabel(): string {
    const status = this.selectedUser?.accountStatus ?? 'no_account';
    if (status === 'registered') {
      return 'Registered';
    }
    if (status === 'invited') {
      return 'Invited';
    }
    return 'No account';
  }

  protected get isStaffEmailDraftValid(): boolean {
    const value = this.staffEmailDraft.trim().toLowerCase();
    return UsersPageComponent.EMAIL_REGEXP.test(value);
  }

  protected get canSendStaffInvite(): boolean {
    if (!this.selectedUser || this.isSelectedClientUser || !this.isSelectedStaffInviteUser) {
      return false;
    }

    if (this.selectedUser.accountStatus === 'registered') {
      return false;
    }

    return this.isStaffEmailDraftValid;
  }

  protected get staffInviteActionLabel(): string {
    if (this.selectedUser?.accountStatus === 'invited') {
      return 'Resend invite';
    }

    return 'Send invite';
  }

  public ngOnInit(): void
  {
    this.rebuildRoleTabs();

    this.subscriptions.add(this.route.queryParamMap.subscribe((params) => {
      const clientId = params.get('clientId')?.trim() ?? '';
      this.pendingFocusClientId = clientId || null;
      this.handledFocusClientId = null;
      if (this.pendingFocusClientId && this.availableRoleTabs.includes('client')) {
        this.activeRoleTab = 'client';
      }
      this.tryFocusClientRow();
    }));

    if (this.isSystemAdmin) {
      this.loadCompanies();
      this.subscriptions.add(this.systemAdminContext.companyId$.pipe(
        distinctUntilChanged(),
        skip(1),
      ).subscribe((companyId) => {
        const normalized = companyId?.trim() ?? '';
        if (!this.isCreateModalOpen && this.form.controls.companyId.value !== normalized) {
          this.form.controls.companyId.setValue(normalized, { emitEvent: false });
        }
        this.load();
        this.loadClinics();
      }));
      this.subscriptions.add(this.form.controls.companyId.valueChanges.subscribe(() => {
        if (!this.isCreateModalOpen) {
          return;
        }
        this.form.controls.clinicId.setValue('');
        this.form.controls.procedureTypeIds.setValue([]);
        this.loadClinics();
        this.loadProcedureTypes();
      }));
    }

    this.subscriptions.add(this.systemAdminContext.clinicId$.pipe(
      distinctUntilChanged(),
      skip(1),
    ).subscribe(() => {
      if (!this.isCreateModalOpen || !this.isClinicRoleSelected) {
        return;
      }
      this.applyCreateScopedClinicDefault(true);
    }));

    this.subscriptions.add(this.form.controls.role.valueChanges.subscribe((role) => {
      if (role !== 'reception' && role !== 'specialist') {
        this.form.controls.clinicId.setValue('');
      } else if (this.isCreateModalOpen) {
        this.applyCreateScopedClinicDefault(false);
      }

      if (role !== 'specialist') {
        this.form.controls.procedureTypeIds.setValue([]);
      }

      this.applyModalModeState();
    }));

    this.load();
  }

  public ngAfterViewInit(): void {
    this.subscriptions.add(
      this.avatarSentinels?.changes.subscribe(() => {
        this.rebindAvatarVisibilityObserver();
      }) ?? new Subscription(),
    );

    this.rebindAvatarVisibilityObserver();
  }

  public ngOnDestroy(): void
  {
    this.avatarVisibilityObserver?.disconnect();
    this.subscriptions.unsubscribe();
  }

  protected selectRoleTab(tabId: string): void {
    if (this.availableRoleTabs.includes(tabId as StaffRole | 'company_admin')) {
      const nextTab = tabId as StaffRole | 'company_admin';
      this.activeRoleTab = nextTab;
      this.load();
    }
  }

  protected openFiltersModal(): void {
    this.filtersForm.reset({ ...this.appliedFilters });
    this.isFiltersModalOpen = true;
  }

  protected closeFiltersModal(): void {
    this.isFiltersModalOpen = false;
  }

  protected applyFilters(): void {
    const value = this.filtersForm.getRawValue();
    this.appliedFilters = {
      query: value.query.trim(),
      userStatus: value.userStatus,
      clientAccountStatus: value.clientAccountStatus,
      clientEmailPresence: value.clientEmailPresence,
      clientOrigin: value.clientOrigin,
      sort: value.sort,
    };
    this.load();
  }

  protected resetFilters(): void {
    const defaults = this.getDefaultFilters();
    this.appliedFilters = defaults;
    this.filtersForm.reset({ ...defaults });
    this.load();
  }

  protected loadMoreClientItems(): void {
    if (!this.pageHasMore || this.isClientPageLoadingMore) {
      return;
    }

    this.loadPage(true);
  }

  protected deleteClientItem(item: UserItem): void {
    const clientId = item.clientProfile?.id ?? '';
    if (item.role !== 'client' || !clientId || item.linkedUserId) {
      return;
    }

    if (!confirm(`Delete client "${item.fullName}"?`)) {
      return;
    }

    this.isClientDeleting = true;
    this.actionErrorText = null;
    this.successText = null;

    this.clientsAdminService.delete(clientId, this.getContextCompanyId() ?? undefined).subscribe({
      next: () => {
        this.isClientDeleting = false;
        if (this.selectedUser?.clientProfile?.id === clientId) {
          this.isCreateModalOpen = false;
          this.selectedUser = null;
        }
        this.successText = 'Client deleted.';
        this.load();
      },
      error: (error: unknown) => {
        this.isClientDeleting = false;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenUserManagement',
          fallback: 'error.generic',
        });
      },
    });
  }

  protected openUserForEdit(item: UserItem): void {
    this.openUser(item);
    this.startEditModal();
  }

  protected setHoveredAvatar(userId: string): void {
    this.hoveredAvatarId = userId;
  }

  protected clearHoveredAvatar(userId: string): void {
    if (this.hoveredAvatarId === userId) {
      this.hoveredAvatarId = null;
    }
  }

  protected isAvatarVisible(userId: string): boolean {
    return this.visibleAvatarIds.has(userId);
  }

  protected openCreateModal(): void {
    if (!this.canCreateForActiveRole) {
      return;
    }

    const preferredRole: StaffRole | 'company_admin' = this.canCreateForActiveRole
      ? this.activeRoleTab
      : (this.creatableRoles[0] ?? 'technician');
    const contextCompanyId = this.getContextCompanyId() ?? '';
    const scopedClinicId = this.getScopedClinicIdFromOptions();

    this.form.patchValue({
      companyId: contextCompanyId,
      email: '',
      phone: '',
      password: '',
      role: preferredRole,
      clinicId: this.isClinicBoundRole(preferredRole) ? scopedClinicId : '',
      procedureTypeIds: [],
      isActive: true,
      fullName: '',
      birthDate: '',
      notes: '',
    });
    this.selectedUser = null;
    this.applyModalModeState();
    this.form.markAsPristine();
    this.actionErrorText = null;
    this.successText = null;
    this.resetSelectedUserPhotoState();
    this.modalMode = 'create';
    this.isCreateModalOpen = true;
    this.loadClinics();
    this.loadProcedureTypes();
  }

  protected closeCreateModal(): void {
    if (this.isSubmitting) {
      return;
    }
    this.hoveredAvatarId = null;
    this.isCreateModalOpen = false;
    this.modalMode = 'create';
    this.selectedUser = null;
    this.resetSelectedUserPhotoState();
    this.resetClientAccountState();
    this.resetStaffAccountState();
    this.applyModalModeState();
    this.loadClinics();
    this.loadProcedureTypes();
    this.loadRatingSettings();
  }

  protected onSubmit(): void
  {
    if (this.form.invalid || this.isSubmitting)
    {
      return;
    }

    if (this.isViewMode) {
      return;
    }

    if (this.isEditMode) {
      this.submitEdit();
      return;
    }

    const value = this.form.getRawValue();
    if (!value.fullName.trim()) {
      this.actionErrorText = 'users.error.fullNameRequired';
      return;
    }

    const targetCompanyId = this.getCreateTargetCompanyId();
    if (this.isSystemAdmin && !targetCompanyId) {
      this.actionErrorText = 'error.settingsCompanyContextRequired';
      return;
    }

    const normalizedEmail = value.email.trim().toLowerCase();
    const normalizedPhone = value.phone.trim();
    const normalizedBirthDate = value.birthDate.trim();
    const normalizedNotes = value.notes.trim();

    if (value.role === 'client' && !normalizedEmail && !normalizedPhone) {
      this.actionErrorText = 'users.error.clientContactRequired';
      return;
    }

    const clinicId = value.role === 'reception' || value.role === 'specialist'
      ? (value.clinicId.trim() ? value.clinicId.trim() : undefined)
      : undefined;
    const procedureTypeIds = value.role === 'specialist'
      ? this.normalizeIdList(value.procedureTypeIds)
      : undefined;

    if (value.role === 'reception' && !clinicId) {
      this.actionErrorText = `Select ${this.clinicLabelLower} for reception`;
      return;
    }

    if (value.role === 'specialist' && !clinicId) {
      this.actionErrorText = `Select ${this.clinicLabelLower} for specialist`;
      return;
    }

    if (value.role === 'specialist' && (!procedureTypeIds || procedureTypeIds.length === 0)) {
      this.actionErrorText = 'Select at least one service type for specialist';
      return;
    }

    this.isSubmitting = true;
    this.actionErrorText = null;
    this.successText = null;

    if (value.role === 'client') {
      this.clientsAdminService.create({
        fullName: value.fullName.trim(),
        birthDate: normalizedBirthDate || undefined,
        email: normalizedEmail || undefined,
        phone: normalizedPhone || undefined,
        notes: normalizedNotes || undefined,
      }, targetCompanyId ?? undefined).subscribe({
        next: (createdClient) => this.finishCreateClientSuccess(createdClient),
        error: (error: unknown) =>
        {
          console.error('Failed to create client', error);
          this.actionErrorText = resolveHttpErrorMessageKey(error, {
            forbidden: 'error.forbiddenUserManagement',
            conflict: 'users.error.clientEmailConflict',
            fallback: 'users.error.clientCreateFailed'
          });
          this.isSubmitting = false;
        }
      });
      return;
    }

    this.usersService.createIntake({
      fullName: value.fullName.trim(),
      birthDate: normalizedBirthDate || undefined,
      email: normalizedEmail,
      phone: normalizedPhone || undefined,
      role: value.role,
      clinicId,
      procedureTypeIds,
    }, targetCompanyId ?? undefined).subscribe({
      next: () =>
      {
        this.finishCreateSuccess(normalizedEmail, value.role);
      },
      error: (error: unknown) =>
      {
        console.error('Failed to create user', error);
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenUserManagement',
          fallback: 'error.userCreateFailed'
        });
        this.isSubmitting = false;
      }
    });
  }

  private finishCreateClientSuccess(client: ClientAdminItem): void {
    const preferredRole: StaffRole | 'company_admin' = this.canCreateForActiveRole
      ? this.activeRoleTab
      : (this.creatableRoles[0] ?? 'technician');
    this.form.patchValue({
      email: '',
      phone: '',
      password: '',
      role: preferredRole,
      clinicId: '',
      procedureTypeIds: [],
      isActive: true,
      fullName: '',
      birthDate: '',
      notes: '',
    });
    this.applyModalModeState();
    this.form.markAsPristine();
    this.isSubmitting = false;
    this.isCreateModalOpen = false;
    this.loadClinics();
    this.loadProcedureTypes();
    this.successText = `Client "${client.fullName}" created.`;
    this.load();
  }

  private finishCreateSuccess(email: string, role: StaffRole | 'company_admin'): void {
        const preferredRole: StaffRole | 'company_admin' = this.canCreateForActiveRole
          ? this.activeRoleTab
          : (this.creatableRoles[0] ?? 'technician');
        this.form.patchValue({
          email: '',
          phone: '',
          password: '',
          role: preferredRole,
          clinicId: '',
          procedureTypeIds: [],
          isActive: true,
          fullName: '',
          birthDate: '',
          notes: '',
        });
        this.applyModalModeState();
        this.form.markAsPristine();
        this.isSubmitting = false;
        this.isCreateModalOpen = false;
        this.loadClinics();
        this.loadProcedureTypes();
        this.successText = role === 'client'
          ? `User "${email}" created.`
          : `Password setup email sent to "${email}".`;
        this.load();
  }

  protected openUser(item: UserItem): void
  {
    const targetCompanyId = this.getContextCompanyId();
    if (this.isSystemAdmin && !targetCompanyId) {
      return;
    }

    this.actionErrorText = null;
    this.successText = null;
    this.selectedUser = item;
    this.resetSelectedUserPhotoState();
    this.modalMode = 'view';
    this.form.patchValue({
      companyId: targetCompanyId ?? '',
      email: item.email,
      phone: item.phone ?? '',
      password: '',
      role: this.toFormRole(item.role),
      clinicId: item.clinicId ?? '',
      procedureTypeIds: item.role === 'specialist'
        ? [...(item.specialistProcedureTypeIds ?? [])]
        : [],
      isActive: item.isActive,
      fullName: item.fullName,
      birthDate: this.normalizeBirthDateForInput(item.birthDate ?? null),
      notes: item.clientProfile?.notes ?? '',
    }, { emitEvent: false });
    this.applyModalModeState();
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.isCreateModalOpen = true;
    this.resetClientAccountState();
    this.resetStaffAccountState();
    this.staffEmailDraft = item.email;
    this.loadClientAccount();
    this.loadClientRatingHistory();
    this.loadClinics();
    this.loadProcedureTypes();
    this.loadRatingSettings();
  }

  protected startEditModal(): void {
    if (!this.selectedUser || !this.canEditModal || this.isSubmitting) {
      return;
    }
    this.modalMode = 'edit';
    this.form.patchValue({ password: '' });
    this.applyModalModeState();
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  protected onClientEmailDraftChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.clientEmailDraft = target?.value ?? '';
  }

  protected onClientRatingDeltaChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const nextValue = Number(target?.value ?? '0');
    this.clientRatingDeltaDraft = Number.isFinite(nextValue) ? nextValue : 0;
    this.clientRatingAdjustmentErrorText = null;
    this.clientRatingAdjustmentSuccessText = null;
  }

  protected onClientRatingNoteChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement | null;
    this.clientRatingNoteDraft = target?.value ?? '';
    this.clientRatingAdjustmentErrorText = null;
    this.clientRatingAdjustmentSuccessText = null;
  }

  protected submitClientRatingAdjustment(): void {
    if (!this.selectedUser || !this.canSubmitClientRatingAdjustment) {
      return;
    }

    const payload: ManualAdjustUserClientRatingRequest = {
      deltaPercent: this.clientRatingDeltaDraft,
      note: this.clientRatingNoteDraft.trim() || undefined,
    };

    this.isClientRatingAdjusting = true;
    this.clientRatingAdjustmentErrorText = null;
    this.clientRatingAdjustmentSuccessText = null;

    this.usersService.manualAdjustClientRating(
      this.selectedUser.id,
      payload,
      this.getContextCompanyId() ?? undefined,
    ).subscribe({
      next: () => {
        this.clientRatingDeltaDraft = 0;
        this.clientRatingNoteDraft = '';
        this.clientRatingAdjustmentSuccessText = 'clientReliability.manualAdjustmentSuccess';
        this.isClientRatingAdjusting = false;
        this.loadClientAccount();
        this.loadClientRatingHistory();
      },
      error: (error: unknown) => {
        this.clientRatingAdjustmentErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'clientReliability.manualAdjustmentError',
        });
        this.isClientRatingAdjusting = false;
      }
    });
  }

  protected onStaffEmailDraftChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.staffEmailDraft = target?.value ?? '';
  }

  protected onSelectedUserPhotoChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const file = target?.files?.[0] ?? null;
    this.selectedUserPhotoFile = file;
    this.selectedUserPhotoName = file?.name ?? '';
    if (target) {
      target.value = '';
    }
  }

  protected uploadSelectedUserPhoto(): void {
    const targetUserId = this.selectedUser?.linkedUserId?.trim() ?? '';
    if (!this.selectedUser || !targetUserId || !this.isEditMode || !this.canEditModal || !this.selectedUserPhotoFile || this.isUserPhotoUploading || this.isUserPhotoRemoving) {
      return;
    }

    this.isUserPhotoUploading = true;
    this.actionErrorText = null;
    this.successText = null;

    this.usersService.uploadPhoto(
      targetUserId,
      this.selectedUserPhotoFile,
      this.getContextCompanyId() ?? undefined,
    ).subscribe({
      next: (updated) => {
        this.selectedUser = updated;
        this.syncSelectedUserIntoItems();
        this.selectedUserPhotoFile = null;
        this.selectedUserPhotoName = '';
        this.isUserPhotoUploading = false;
        this.successText = 'Profile photo uploaded.';
      },
      error: (error: unknown) => {
        this.isUserPhotoUploading = false;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'error.generic',
        });
      },
    });
  }

  protected removeSelectedUserPhoto(): void {
    const targetUserId = this.selectedUser?.linkedUserId?.trim() ?? '';
    if (!this.selectedUser || !targetUserId || !this.isEditMode || !this.canEditModal || !this.selectedUser.photoUrl || this.isUserPhotoUploading || this.isUserPhotoRemoving) {
      return;
    }

    this.isUserPhotoRemoving = true;
    this.actionErrorText = null;
    this.successText = null;

    this.usersService.removePhoto(
      targetUserId,
      this.getContextCompanyId() ?? undefined,
    ).subscribe({
      next: (updated) => {
        this.selectedUser = updated;
        this.syncSelectedUserIntoItems();
        this.selectedUserPhotoFile = null;
        this.selectedUserPhotoName = '';
        this.isUserPhotoRemoving = false;
        this.successText = 'Profile photo removed.';
      },
      error: (error: unknown) => {
        this.isUserPhotoRemoving = false;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'error.generic',
        });
      },
    });
  }

  protected saveClientEmail(): void {
    if (!this.clientAccount || !this.isClientEmailDraftValid || this.isClientEmailSaving) {
      return;
    }

    const email = this.clientEmailDraft.trim().toLowerCase();
    this.isClientEmailSaving = true;
    this.clientAccountActionErrorText = null;
    this.clientAccountSuccessText = null;

    this.clientsAdminService.updateContact(
      this.clientAccount.id,
      { email },
      this.getContextCompanyId() ?? undefined,
    ).subscribe({
      next: (client) => {
        this.clientAccount = client;
        this.clientEmailDraft = client.email ?? '';
        this.applyClientAccountToSelectedUser(client);
        this.clientAccountSuccessText = `Email updated to "${client.email ?? ''}".`;
        this.isClientEmailSaving = false;
      },
      error: (error: unknown) => {
        this.isClientEmailSaving = false;
        this.clientAccountActionErrorText = resolveHttpErrorMessageKey(error, {
          conflict: 'Email is already used by another client',
          fallback: 'error.generic',
        });
      },
    });
  }

  protected sendClientInvite(): void {
    if (!this.clientAccount || !this.canSendClientInvite || this.isClientInviteSending) {
      return;
    }

    this.isClientInviteSending = true;
    this.clientAccountActionErrorText = null;
    this.clientAccountSuccessText = null;

    this.clientsAdminService.sendInvite(
      this.clientAccount.id,
      {},
      this.getContextCompanyId() ?? undefined,
    ).subscribe({
      next: (response) => {
        this.clientAccountSuccessText =
          `Invite sent. Expires at ${new Date(response.expiresAt).toLocaleString()}.`;
        this.isClientInviteSending = false;
        this.loadClientAccount();
      },
      error: (error: unknown) => {
        this.isClientInviteSending = false;
        this.clientAccountActionErrorText = resolveHttpErrorMessageKey(error, {
          conflict: 'Invite conflict: email occupied or client already linked',
          fallback: 'error.generic',
        });
      },
    });
  }

  protected createSelectedClientTelegramLinkToken(): void {
    if (!this.clientAccount || !this.canManageSelectedClientTelegramLink || this.isClientTelegramTokenCreating) {
      return;
    }

    this.isClientTelegramTokenCreating = true;
    this.clientAccountActionErrorText = null;
    this.clientAccountSuccessText = null;

    this.telegramConnectorService.createClientLinkToken(
      this.clientAccount.id,
      this.getContextCompanyId() ?? undefined,
    ).subscribe({
      next: (token) => {
        this.clientTelegramLinkToken = token;
        void this.updateSelectedClientTelegramQr(token.linkUrl);
        this.clientAccountSuccessText = 'Telegram connection link generated.';
        this.isClientTelegramTokenCreating = false;
      },
      error: (error: unknown) => {
        this.isClientTelegramTokenCreating = false;
        this.clientAccountActionErrorText = this.resolveTelegramError(error);
      },
    });
  }

  protected copySelectedClientTelegramLink(): void {
    const linkUrl = this.clientTelegramLinkToken?.linkUrl;
    if (!linkUrl || this.isClientTelegramLinkCopying) {
      return;
    }

    this.isClientTelegramLinkCopying = true;
    this.clientAccountActionErrorText = null;
    this.clientAccountSuccessText = null;

    if (!navigator.clipboard) {
      this.clientAccountActionErrorText = 'error.generic';
      this.isClientTelegramLinkCopying = false;
      return;
    }

    void navigator.clipboard.writeText(linkUrl)
      .then(() => {
        this.clientAccountSuccessText = 'Telegram connection link copied.';
      })
      .catch(() => {
        this.clientAccountActionErrorText = 'error.generic';
      })
      .finally(() => {
        this.isClientTelegramLinkCopying = false;
      });
  }

  protected revokeSelectedClientTelegramLink(): void {
    if (!this.clientAccount || !this.canManageSelectedClientTelegramLink || this.isClientTelegramRevoking) {
      return;
    }

    this.isClientTelegramRevoking = true;
    this.clientAccountActionErrorText = null;
    this.clientAccountSuccessText = null;

    this.telegramConnectorService.revokeClientLink(
      this.clientAccount.id,
      this.getContextCompanyId() ?? undefined,
    ).subscribe({
      next: (link) => {
        this.clientTelegramLink = link;
        this.syncClientTelegramCommunicationChannel(link);
        this.clientTelegramLinkToken = null;
        this.clientTelegramQrDataUrl = null;
        this.clientAccountSuccessText = 'Telegram link revoked.';
        this.isClientTelegramRevoking = false;
      },
      error: (error: unknown) => {
        this.isClientTelegramRevoking = false;
        this.clientAccountActionErrorText = this.resolveTelegramError(error);
      },
    });
  }

  protected saveStaffEmail(): void {
    if (!this.selectedUser || !this.isSelectedStaffInviteUser || !this.isStaffEmailDraftValid || this.isStaffEmailSaving) {
      return;
    }

    const email = this.staffEmailDraft.trim().toLowerCase();
    const phone = this.form.controls.phone.value.trim();

    this.isStaffEmailSaving = true;
    this.staffAccountActionErrorText = null;
    this.staffAccountSuccessText = null;

    this.usersService.updateContact(
      this.selectedUser.id,
      { email, phone: phone || undefined },
      this.getContextCompanyId() ?? undefined,
    ).subscribe({
      next: (updated) => {
        this.selectedUser = updated;
        this.syncSelectedUserIntoItems();
        this.form.patchValue({
          email: updated.email,
          phone: updated.phone ?? '',
        }, { emitEvent: false });
        this.staffEmailDraft = updated.email;
        this.staffAccountSuccessText = `Email updated to "${updated.email}".`;
        this.isStaffEmailSaving = false;
      },
      error: (error: unknown) => {
        this.isStaffEmailSaving = false;
        this.staffAccountActionErrorText = resolveHttpErrorMessageKey(error, {
          conflict: 'Email is already used by another account',
          fallback: 'error.generic',
        });
      },
    });
  }

  protected sendStaffInvite(): void {
    if (!this.selectedUser || !this.canSendStaffInvite || this.isStaffInviteSending) {
      return;
    }

    const email = this.staffEmailDraft.trim().toLowerCase();
    this.isStaffInviteSending = true;
    this.staffAccountActionErrorText = null;
    this.staffAccountSuccessText = null;

    this.usersService.sendInvite(
      this.selectedUser.id,
      { email },
      this.getContextCompanyId() ?? undefined,
    ).subscribe({
      next: (response) => {
        this.staffAccountSuccessText = `Password setup email sent. Expires at ${new Date(response.expiresAt).toLocaleString()}.`;
        this.isStaffInviteSending = false;
        const selectedUserId = this.selectedUser?.id;
        if (!selectedUserId) {
          return;
        }

        this.usersService.getById(
          selectedUserId,
          this.getContextCompanyId() ?? undefined,
        ).subscribe({
          next: (fresh) => {
            this.selectedUser = fresh;
            this.syncSelectedUserIntoItems();
            this.form.patchValue({
              email: fresh.email,
              phone: fresh.phone ?? '',
            }, { emitEvent: false });
            this.staffEmailDraft = fresh.email;
          },
          error: () => {
          },
        });
      },
      error: (error: unknown) => {
        this.isStaffInviteSending = false;
        this.staffAccountActionErrorText = resolveHttpErrorMessageKey(error, {
          conflict: 'Invite conflict: email occupied or user already registered',
          fallback: 'error.generic',
        });
      },
    });
  }

  protected toggleUserActive(event: MouseEvent, item: UserItem): void
  {
    event.stopPropagation();

    if (item.sourceKind !== 'user') {
      return;
    }

    const nextIsActive = !item.isActive;
    const confirmEmailRaw = window.prompt(
      `Type current user email to ${nextIsActive ? 'enable' : 'disable'}: ${item.email}`,
      '',
    );

    if (confirmEmailRaw === null)
    {
      return;
    }

    const confirmEmail = confirmEmailRaw.trim().toLowerCase();
    if (!confirmEmail)
    {
      return;
    }

    this.actionErrorText = null;
    this.successText = null;

    this.usersService.updateActive(item.id, {
      isActive: nextIsActive,
      confirmEmail
    }, this.getContextCompanyId() ?? undefined).subscribe({
      next: () =>
      {
        this.successText = `User "${item.email}" ${nextIsActive ? 'enabled' : 'disabled'}.`;
        this.load();
      },
      error: (error: unknown) =>
      {
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenUserManagement',
          fallback: 'error.generic'
        });
      }
    });
  }

  protected load(): void
  {
    const targetCompanyId = this.getContextCompanyId();
    if (this.isSystemAdmin && !targetCompanyId) {
      this.items = [];
      this.isLoading = false;
      this.errorText = 'error.settingsCompanyContextRequired';
      return;
    }

    this.resetPageState();
    this.loadPage(false);
  }

  private submitEdit(): void {
    if (!this.selectedUser) {
      return;
    }

    const value = this.form.getRawValue();
    const targetCompanyId = this.getContextCompanyId();
    const payload = {
      confirmEmail: this.selectedUser.email.trim().toLowerCase(),
      email: value.email.trim().toLowerCase(),
      password: value.password.trim() ? value.password.trim() : undefined,
      phone: value.phone.trim() ? value.phone.trim() : undefined,
      fullName: value.fullName.trim() ? value.fullName.trim() : undefined,
      birthDate: value.birthDate.trim() ? value.birthDate.trim() : undefined,
      notes: this.selectedUser.role === 'client'
        ? value.notes.trim()
        : undefined,
      clinicId: this.selectedUser.role === 'specialist'
        ? (value.clinicId.trim() ? value.clinicId.trim() : undefined)
        : undefined,
      procedureTypeIds: this.selectedUser.role === 'specialist'
        ? this.normalizeIdList(value.procedureTypeIds)
        : undefined,
    };

    if (!value.fullName.trim()) {
      this.actionErrorText = 'users.error.fullNameRequired';
      return;
    }

    if (this.selectedUser.role === 'specialist' && (!payload.procedureTypeIds || payload.procedureTypeIds.length === 0)) {
      this.actionErrorText = 'Select at least one service type for specialist';
      return;
    }

    if (this.selectedUser.role === 'specialist' && !payload.clinicId) {
      this.actionErrorText = `Select ${this.clinicLabelLower} for specialist`;
      return;
    }

    this.isSubmitting = true;
    this.actionErrorText = null;
    this.successText = null;

    if (this.selectedUser.role === 'client') {
      const clientId = this.selectedUser.clientProfile?.id ?? '';
      if (!clientId) {
        this.isSubmitting = false;
        this.actionErrorText = 'users.error.clientResolveFailed';
        return;
      }

      this.clientsAdminService.update(clientId, {
        fullName: value.fullName.trim() ? value.fullName.trim() : undefined,
        phone: value.phone.trim() ? value.phone.trim() : undefined,
        birthDate: value.birthDate.trim() ? value.birthDate.trim() : undefined,
        notes: value.notes.trim() ? value.notes.trim() : undefined,
      }, targetCompanyId ?? undefined).subscribe({
        next: (updatedClient) => {
          this.isSubmitting = false;
          this.successText = `Client "${updatedClient.fullName}" updated.`;
          this.selectedUser = this.mapClientToUserItem(updatedClient);
          this.syncSelectedUserIntoItems();
          this.modalMode = 'view';
          this.form.patchValue({
            email: updatedClient.email ?? '',
            phone: updatedClient.phone ?? '',
            password: '',
            role: 'client',
            clinicId: '',
            procedureTypeIds: [],
            isActive: false,
            fullName: updatedClient.fullName,
            birthDate: updatedClient.birthDate ?? '',
            notes: updatedClient.notes ?? '',
          }, { emitEvent: false });
          this.applyModalModeState();
          this.form.markAsPristine();
          this.form.markAsUntouched();
          this.loadClientAccount();
        },
        error: (error: unknown) => {
          this.isSubmitting = false;
          this.actionErrorText = resolveHttpErrorMessageKey(error, {
            forbidden: 'error.forbiddenUserManagement',
            fallback: 'error.generic'
          });
        },
      });
      return;
    }

    this.usersService.update(this.selectedUser.id, payload, targetCompanyId ?? undefined).subscribe({
      next: (updated) => {
        this.isSubmitting = false;
        this.successText = `User "${updated.email}" updated.`;
        this.selectedUser = updated;
        this.syncSelectedUserIntoItems();
        this.modalMode = 'view';
        this.form.patchValue({
          email: updated.email,
          phone: updated.phone ?? '',
          password: '',
          role: this.toFormRole(updated.role),
          clinicId: updated.clinicId ?? '',
          procedureTypeIds: updated.role === 'specialist'
            ? [...(updated.specialistProcedureTypeIds ?? [])]
            : [],
          isActive: updated.isActive,
          fullName: updated.fullName,
          birthDate: this.normalizeBirthDateForInput(updated.birthDate ?? null),
          notes: updated.clientProfile?.notes ?? '',
        }, { emitEvent: false });
        this.applyModalModeState();
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.loadClientAccount();
      },
      error: (error: unknown) => {
        this.isSubmitting = false;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenUserManagement',
          fallback: 'error.generic'
        });
      }
    });
  }

  private loadClinics(): void
  {
    const targetCompanyId = this.isSystemAdmin
      ? (this.isCreateModalOpen ? this.getCreateTargetCompanyId() : this.getContextCompanyId())
      : null;
    const cacheKey = this.getModalContextCacheKey(targetCompanyId);
    if (this.isSystemAdmin && !targetCompanyId) {
      this.clinicOptions = [];
      this.clinicLabel = UsersPageComponent.DEFAULT_CLINIC_LABEL;
      return;
    }

    const cached = this.clinicsCache.get(cacheKey);
    if (cached) {
      this.clinicLabel = cached.clinicLabel;
      this.clinicOptions = cached.items;
      this.applyClinicNamesFromOptions();
      this.applyCreateScopedClinicDefault(false);
      return;
    }

    if (this.loadingClinicKeys.has(cacheKey)) {
      return;
    }

    this.loadingClinicKeys.add(cacheKey);

    forkJoin({
      operations: this.settingsService.getOperationsSettings(targetCompanyId ?? undefined),
      clinics: this.settingsService.getClinicOperationalSettings(targetCompanyId ?? undefined),
    }).subscribe({
      next: ({ operations, clinics }) => {
        const nextValue = {
          clinicLabel: this.normalizeLabel(
            operations.clinicLabel,
            UsersPageComponent.DEFAULT_CLINIC_LABEL,
          ),
          items: clinics,
        };
        this.clinicsCache.set(cacheKey, nextValue);
        if (this.getModalContextCacheKey(
          this.isSystemAdmin
            ? (this.isCreateModalOpen ? this.getCreateTargetCompanyId() : this.getContextCompanyId())
            : null,
        ) === cacheKey) {
          this.clinicLabel = nextValue.clinicLabel;
          this.clinicOptions = nextValue.items;
          this.applyClinicNamesFromOptions();
          this.applyCreateScopedClinicDefault(false);
        }
        this.loadingClinicKeys.delete(cacheKey);
      },
      error: () => {
        if (this.getModalContextCacheKey(
          this.isSystemAdmin
            ? (this.isCreateModalOpen ? this.getCreateTargetCompanyId() : this.getContextCompanyId())
            : null,
        ) === cacheKey) {
          this.clinicLabel = UsersPageComponent.DEFAULT_CLINIC_LABEL;
          this.clinicOptions = [];
        }
        this.loadingClinicKeys.delete(cacheKey);
      },
    });
  }

  private loadProcedureTypes(): void
  {
    const targetCompanyId = this.isSystemAdmin
      ? (this.isCreateModalOpen ? this.getCreateTargetCompanyId() : this.getContextCompanyId())
      : null;
    const cacheKey = this.getModalContextCacheKey(targetCompanyId);
    if (this.isSystemAdmin && !targetCompanyId) {
      this.procedureTypeOptions = [];
      return;
    }

    const cached = this.procedureTypesCache.get(cacheKey);
    if (cached) {
      this.procedureTypeOptions = cached;
      return;
    }

    if (this.loadingProcedureTypeKeys.has(cacheKey)) {
      return;
    }

    this.loadingProcedureTypeKeys.add(cacheKey);

    this.procedureTypesService.getList({
      companyId: targetCompanyId ?? undefined,
    }).subscribe({
      next: (items: ProcedureTypeItem[]) => {
        const nextValue = items.filter((item) => item.isActive);
        this.procedureTypesCache.set(cacheKey, nextValue);
        if (this.getModalContextCacheKey(
          this.isSystemAdmin
            ? (this.isCreateModalOpen ? this.getCreateTargetCompanyId() : this.getContextCompanyId())
            : null,
        ) === cacheKey) {
          this.procedureTypeOptions = nextValue;
        }
        this.loadingProcedureTypeKeys.delete(cacheKey);
      },
      error: () => {
        if (this.getModalContextCacheKey(
          this.isSystemAdmin
            ? (this.isCreateModalOpen ? this.getCreateTargetCompanyId() : this.getContextCompanyId())
            : null,
        ) === cacheKey) {
          this.procedureTypeOptions = [];
        }
        this.loadingProcedureTypeKeys.delete(cacheKey);
      },
    });
  }

  private loadRatingSettings(): void {
    const targetCompanyId = this.isSystemAdmin
      ? (this.isCreateModalOpen ? this.getCreateTargetCompanyId() : this.getContextCompanyId())
      : null;
    const cacheKey = this.getModalContextCacheKey(targetCompanyId);
    if (this.isSystemAdmin && !targetCompanyId) {
      this.ratingSettings = null;
      return;
    }

    if (this.ratingSettingsCache.has(cacheKey)) {
      this.ratingSettings = this.ratingSettingsCache.get(cacheKey) ?? null;
      return;
    }

    if (this.loadingRatingSettingsKeys.has(cacheKey)) {
      return;
    }

    this.loadingRatingSettingsKeys.add(cacheKey);

    this.settingsService.getRatingSettings(targetCompanyId ?? undefined).subscribe({
      next: (settings) => {
        this.ratingSettingsCache.set(cacheKey, settings);
        if (this.getModalContextCacheKey(
          this.isSystemAdmin
            ? (this.isCreateModalOpen ? this.getCreateTargetCompanyId() : this.getContextCompanyId())
            : null,
        ) === cacheKey) {
          this.ratingSettings = settings;
        }
        this.loadingRatingSettingsKeys.delete(cacheKey);
      },
      error: () => {
        this.ratingSettingsCache.set(cacheKey, null);
        if (this.getModalContextCacheKey(
          this.isSystemAdmin
            ? (this.isCreateModalOpen ? this.getCreateTargetCompanyId() : this.getContextCompanyId())
            : null,
        ) === cacheKey) {
          this.ratingSettings = null;
        }
        this.loadingRatingSettingsKeys.delete(cacheKey);
      },
    });
  }

  private tryFocusClientRow(): void
  {
    if (!this.pendingFocusClientId || this.handledFocusClientId === this.pendingFocusClientId) {
      return;
    }

    const target = this.items.find(
      (item) =>
        item.id === this.pendingFocusClientId ||
        item.clientProfile?.id === this.pendingFocusClientId,
    );
    if (!target) {
      return;
    }

    this.openUser(target);
    this.handledFocusClientId = this.pendingFocusClientId;
  }

  private loadCompanies(): void
  {
    this.companiesService.getList().subscribe({
      next: (items: CompanyItem[]) => {
        this.companies = items;
      },
      error: () => {
        this.companies = [];
      },
    });
  }

  private getContextCompanyId(): string | null {
    if (!this.isSystemAdmin) {
      return null;
    }
    const companyId = this.systemAdminContext.getCompanyId()?.trim() ?? '';
    return companyId || null;
  }

  private getCreateTargetCompanyId(): string | null {
    if (!this.isSystemAdmin) {
      return null;
    }
    const companyId = this.form.controls.companyId.value.trim();
    return companyId || null;
  }

  private isClinicBoundRole(role: StaffRole | 'company_admin'): boolean {
    return role === 'reception' || role === 'specialist';
  }

  private getScopedClinicIdFromOptions(): string {
    const clinicId = this.systemAdminContext.getClinicId()?.trim() ?? '';
    if (!clinicId) {
      return '';
    }

    return this.clinicOptions.some((clinic) => clinic.clinicId === clinicId)
      ? clinicId
      : '';
  }

  private applyCreateScopedClinicDefault(force: boolean): void {
    if (!this.isCreateModalOpen || !this.isClinicRoleSelected) {
      return;
    }

    const currentClinicId = this.form.controls.clinicId.value.trim();
    if (currentClinicId && !force) {
      return;
    }

    const scopedClinicId = this.getScopedClinicIdFromOptions();
    if (scopedClinicId !== currentClinicId) {
      this.form.controls.clinicId.setValue(scopedClinicId);
    }
  }

  protected get companyContextMissing(): boolean {
    return this.isSystemAdmin && !this.getContextCompanyId();
  }

  private normalizeIdList(items: string[] | null | undefined): string[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return Array.from(
      new Set(
        items
          .map((item) => item.trim())
          .filter((item) => !!item),
      ),
    );
  }

  private normalizeLabel(value: string | null | undefined, fallback: string): string {
    const normalized = (value ?? '').trim().replace(/\s+/g, ' ');
    return normalized || fallback;
  }

  protected clinicDisplayName(item: UserItem): string {
    const clinicId = item.clinicId?.trim() ?? '';
    if (!clinicId) {
      return '—';
    }

    return item.clinicName?.trim()
      || this.clinicOptions.find((option) => option.clinicId === clinicId)?.clinicName
      || '—';
  }

  private ensureClinicLabelsForItems(items: UserItem[]): void {
    const hasMissingClinicLabel = items.some((item) =>
      !!item.clinicId?.trim() && !item.clinicName?.trim(),
    );

    if (!hasMissingClinicLabel) {
      return;
    }

    this.loadClinics();
  }

  private applyClinicNamesFromOptions(): void {
    if (this.clinicOptions.length === 0) {
      return;
    }

    const clinicNamesById = new Map(
      this.clinicOptions.map((clinic) => [clinic.clinicId, clinic.clinicName]),
    );

    this.items = this.items.map((item) => {
      const clinicId = item.clinicId?.trim() ?? '';
      if (!clinicId || item.clinicName?.trim()) {
        return item;
      }

      const clinicName = clinicNamesById.get(clinicId)?.trim() ?? '';
      return clinicName ? { ...item, clinicName } : item;
    });

    if (this.selectedUser) {
      const selectedClinicId = this.selectedUser.clinicId?.trim() ?? '';
      if (selectedClinicId && !this.selectedUser.clinicName?.trim()) {
        const clinicName = clinicNamesById.get(selectedClinicId)?.trim() ?? '';
        if (clinicName) {
          this.selectedUser = { ...this.selectedUser, clinicName };
        }
      }
    }
  }

  protected readableId(value: string | null | undefined): string {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (normalized.length > 18) {
      return `${normalized.slice(0, 8)}...${normalized.slice(-6)}`;
    }

    return normalized;
  }

  protected clientStatusLabel(item: UserItem): string {
    if (item.accountStatus === 'registered') {
      return 'Registered';
    }
    if (item.accountStatus === 'invited') {
      return 'Invited';
    }
    return 'No account';
  }

  protected clientReliabilityLabel(item: UserItem): string {
    const score = item.clientProfile?.ratingPercent ?? 100;

    if (score >= 85) {
      return 'clientReliability.label.reliable';
    }

    if (score >= 60) {
      return 'clientReliability.label.watch';
    }

    return 'clientReliability.label.risk';
  }

  protected clientReliabilityTone(item: UserItem): 'good' | 'warn' | 'risk' {
    const score = item.clientProfile?.ratingPercent ?? 100;

    if (score >= 85) {
      return 'good';
    }

    if (score >= 60) {
      return 'warn';
    }

    return 'risk';
  }

  protected clientRatingEventLabel(event: UserClientRatingEvent): string {
    switch (event.eventType) {
      case 'late_cancel':
        return 'clientReliability.event.lateCancel';
      case 'no_show':
        return 'clientReliability.event.noShow';
      case 'late_arrival':
        return 'clientReliability.event.lateArrival';
      case 'client_reschedule':
        return 'clientReliability.event.clientReschedule';
      case 'attended_on_time':
        return 'clientReliability.event.attendedOnTime';
      case 'manual_adjustment':
        return 'clientReliability.event.manualAdjustment';
      default:
        return event.eventType;
    }
  }

  protected clientRatingEventMeta(event: UserClientRatingEvent): number | null {
    if (event.eventType !== 'client_reschedule') {
      return null;
    }

    const rawStep = event.payloadJson?.['churnStep'];
    const churnStep = typeof rawStep === 'number'
      ? rawStep
      : typeof rawStep === 'string'
        ? Number(rawStep)
        : NaN;

    if (!Number.isFinite(churnStep) || churnStep <= 0) {
      return null;
    }

    return Math.trunc(churnStep);
  }

  protected userAvatarText(item: UserItem): string {
    const base = (item.fullName || item.email || '').trim();
    if (!base) {
      return '—';
    }

    const parts = base.split(/\s+/).filter((part) => !!part);
    if (parts.length >= 2) {
      return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
    }

    return base.slice(0, 2).toUpperCase();
  }

  protected isProcedureTypeSelected(procedureTypeId: string): boolean {
    return this.form.controls.procedureTypeIds.value.includes(procedureTypeId);
  }

  protected onProcedureTypeToggle(procedureTypeId: string, checked: boolean): void {
    const current = this.normalizeIdList(this.form.controls.procedureTypeIds.value);

    if (checked) {
      if (!current.includes(procedureTypeId)) {
        this.form.controls.procedureTypeIds.setValue([...current, procedureTypeId]);
      }
      return;
    }

    this.form.controls.procedureTypeIds.setValue(
      current.filter((id) => id !== procedureTypeId),
    );
  }

  protected get selectedProcedureTypeNamesText(): string {
    const selectedIds = new Set(this.form.controls.procedureTypeIds.value);
    const names = this.procedureTypeOptions
      .filter((item) => selectedIds.has(item.id))
      .map((item) => item.name);

    return names.join(', ');
  }

  private mapClientToUserItem(client: ClientAdminItem): UserItem {
    return {
      id: `client:${client.id}`,
      sourceKind: 'client',
      linkedUserId: client.userId ?? null,
      clinicId: null,
      clinicName: null,
      photoUrl: client.photoUrl ?? null,
      fullName: client.fullName,
      birthDate: client.birthDate,
      email: client.email ?? '',
      phone: client.phone ?? null,
      role: 'client',
      isActive: false,
      accountStatus: client.accountStatus,
      inviteExpiresAt: client.inviteExpiresAt,
      accountOrigin: client.accountOrigin,
      clientProfile: {
        id: client.id,
        fullName: client.fullName,
        ratingPercent: client.ratingPercent,
        birthDate: client.birthDate,
        notes: client.notes,
      },
      specialistProcedureTypeIds: [],
      createdAt: client.createdAt,
      updatedAt: client.updatedAt,
    };
  }

  private resetPageState(): void {
    this.pageCursor = null;
    this.pageHasMore = false;
  }

  private getDefaultFilters(): UserManagementFilterState {
    return {
      query: '',
      userStatus: '',
      clientAccountStatus: '',
      clientEmailPresence: '',
      clientOrigin: '',
      sort: 'createdAtDesc',
    };
  }

  private loadPage(append: boolean): void {
    const targetCompanyId = this.getContextCompanyId();
    const limit = 20;

    if (append) {
      this.isClientPageLoadingMore = true;
    } else {
      this.isLoading = true;
      this.errorText = null;
    }

    if (this.isClientTabActive) {
      this.clientsAdminService.getPage({
          companyId: targetCompanyId ?? undefined,
          limit,
          cursor: append ? this.pageCursor : null,
          query: this.appliedFilters.query,
          accountStatus: this.appliedFilters.clientAccountStatus,
          emailPresence: this.appliedFilters.clientEmailPresence,
          origin: this.appliedFilters.clientOrigin,
          sort: this.appliedFilters.sort,
        })
        .subscribe({
          next: ({ items, nextCursor, hasMore }) => {
            this.handleLoadedPage(
              append,
              items.map((item) => this.mapClientToUserItem(item)),
              nextCursor,
              hasMore,
            );
          },
          error: (error: unknown) => this.handlePageLoadError(error),
        });
      return;
    }

    this.usersService.getPage({
      companyId: targetCompanyId ?? undefined,
      limit,
      cursor: append ? this.pageCursor : null,
      role: this.activeRoleTab,
      status: this.appliedFilters.userStatus,
      query: this.appliedFilters.query,
      sort: this.appliedFilters.sort,
    }).subscribe({
      next: ({ items, nextCursor, hasMore }) => {
        this.handleLoadedPage(append, items, nextCursor, hasMore);
      },
      error: (error: unknown) => this.handlePageLoadError(error),
    });
  }

  private handleLoadedPage(
    append: boolean,
    items: UserItem[],
    nextCursor: string | null,
    hasMore: boolean,
  ): void {
    this.items = append
      ? this.appendClientPageItems(this.items, items)
      : items;
    this.pageCursor = nextCursor;
    this.pageHasMore = hasMore;
    this.hoveredAvatarId = null;
    this.ensureClinicLabelsForItems(this.items);
    this.rebindAvatarVisibilityObserver();
    this.tryFocusClientRow();
    this.isLoading = false;
    this.isClientPageLoadingMore = false;
    this.ensurePendingFocusClientLoaded();
  }

  private handlePageLoadError(error: unknown): void {
    console.error('Failed to load users', error);
    this.errorText = resolveHttpErrorMessageKey(error, {
      forbidden: 'error.forbiddenUserManagement',
      fallback: 'error.userListLoadFailed',
    });
    this.isLoading = false;
    this.isClientPageLoadingMore = false;
  }

  private ensurePendingFocusClientLoaded(): void {
    const pendingClientId = this.pendingFocusClientId?.trim() ?? '';
    if (!pendingClientId || !this.isClientTabActive) {
      return;
    }

    const alreadyPresent = this.items.some(
      (item) => item.id === pendingClientId || item.clientProfile?.id === pendingClientId,
    );
    if (alreadyPresent || this.handledFocusClientId === pendingClientId) {
      return;
    }

    this.clientsAdminService.getById(
      pendingClientId,
      this.getContextCompanyId() ?? undefined,
    ).subscribe({
      next: (client) => {
        const mapped = this.mapClientToUserItem(client);
        this.items = [mapped, ...this.items.filter((item) => item.id !== mapped.id)];
        this.rebindAvatarVisibilityObserver();
        this.tryFocusClientRow();
      },
      error: () => {
      },
    });
  }

  private appendClientPageItems(currentItems: UserItem[], nextItems: UserItem[]): UserItem[] {
    if (currentItems.length === 0) {
      return [...nextItems];
    }

    const byId = new Map(currentItems.map((item) => [item.id, item]));
    for (const item of nextItems) {
      byId.set(item.id, item);
    }

    const seen = new Set<string>();
    const merged: UserItem[] = [];

    for (const item of currentItems) {
      const nextItem = byId.get(item.id) ?? item;
      if (seen.has(nextItem.id)) {
        continue;
      }
      seen.add(nextItem.id);
      merged.push(nextItem);
    }

    for (const item of nextItems) {
      if (seen.has(item.id)) {
        continue;
      }
      seen.add(item.id);
      merged.push(item);
    }

    return merged;
  }

  private roleTabLabel(role: StaffRole | 'company_admin'): string {
    switch (role) {
      case 'company_admin':
        return 'Company Admin';
      case 'manager':
        return 'Manager';
      case 'technician':
        return 'Technician';
      case 'specialist':
        return 'Specialist';
      case 'reception':
        return 'Reception';
      case 'client':
        return 'Client';
      default:
        return 'User';
    }
  }

  private rebuildRoleTabs(): void {
    this.roleTabs = this.availableRoleTabs.map((role) => ({
      id: role,
      label: this.roleTabLabel(role),
    }));

    if (this.availableRoleTabs.length > 0) {
      this.activeRoleTab = this.availableRoleTabs[0];
    }
  }

  private get availableRoleTabs(): Array<StaffRole | 'company_admin'> {
    return this.isSystemAdmin
      ? UsersPageComponent.SYSTEM_ADMIN_ROLE_TABS
      : this.creatableRoles;
  }

  private lowerFirst(value: string): string {
    return value ? value.charAt(0).toLowerCase() + value.slice(1) : value;
  }

  private getModalContextCacheKey(targetCompanyId: string | null): string {
    return targetCompanyId?.trim() || '__default__';
  }

  private normalizeBirthDateForInput(value: string | null): string {
    if (!value) {
      return '';
    }

    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    const year = parsed.getUTCFullYear();
    const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
    const day = String(parsed.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private isAllowedRole(role: UserRole | null): boolean {
    return role === 'system_admin' || role === 'company_admin' || role === 'manager';
  }

  private toFormRole(role: UserItem['role']): StaffRole | 'company_admin' {
    return role === 'system_admin' ? 'manager' : role;
  }

  private applyModalModeState(): void {
    const controls = this.form.controls;
    const isClientEntity = !this.isCreateMode && this.selectedUser?.role === 'client';
    const isCreateClient = this.isCreateMode && controls.role.value === 'client';

    if (isClientEntity || isCreateClient) {
      controls.email.setValidators([Validators.email]);
      controls.password.clearValidators();
    } else {
      controls.email.setValidators([Validators.required, Validators.email]);
      controls.password.setValidators([Validators.minLength(8)]);
    }
    controls.email.updateValueAndValidity({ emitEvent: false });
    controls.password.updateValueAndValidity({ emitEvent: false });

    if (this.isCreateMode) {
      controls.companyId.enable({ emitEvent: false });
      controls.email.enable({ emitEvent: false });
      controls.phone.enable({ emitEvent: false });
      controls.password.enable({ emitEvent: false });
      controls.role.enable({ emitEvent: false });
      controls.clinicId.enable({ emitEvent: false });
      controls.procedureTypeIds.enable({ emitEvent: false });
      controls.isActive.enable({ emitEvent: false });
      controls.fullName.enable({ emitEvent: false });
      controls.birthDate.enable({ emitEvent: false });
      controls.notes.enable({ emitEvent: false });
      return;
    }

    controls.companyId.disable({ emitEvent: false });
    controls.email.disable({ emitEvent: false });
    controls.phone.disable({ emitEvent: false });
    controls.password.disable({ emitEvent: false });
    controls.role.disable({ emitEvent: false });
    controls.clinicId.disable({ emitEvent: false });
    controls.procedureTypeIds.disable({ emitEvent: false });
    controls.isActive.disable({ emitEvent: false });
    controls.fullName.disable({ emitEvent: false });
    controls.birthDate.disable({ emitEvent: false });
    controls.notes.disable({ emitEvent: false });

    if (this.isEditMode) {
      controls.email.enable({ emitEvent: false });
      controls.phone.enable({ emitEvent: false });
      controls.password.enable({ emitEvent: false });
      controls.fullName.enable({ emitEvent: false });
      controls.birthDate.enable({ emitEvent: false });
      if (this.selectedUser?.role === 'specialist') {
        controls.clinicId.enable({ emitEvent: false });
        controls.procedureTypeIds.enable({ emitEvent: false });
      }
      if (this.selectedUser?.role === 'client') {
        controls.notes.enable({ emitEvent: false });
      }
    }
  }

  private loadClientAccount(): void {
    const clientId = this.selectedUser?.role === 'client'
      ? (this.selectedUser.clientProfile?.id ?? '')
      : '';

    if (!clientId) {
      this.resetClientAccountState();
      return;
    }

    this.isClientAccountLoading = true;
    this.clientAccountActionErrorText = null;
    this.clientAccountSuccessText = null;

    this.clientsAdminService.getById(
      clientId,
      this.getContextCompanyId() ?? undefined,
    ).subscribe({
      next: (client) => {
        this.clientAccount = client;
        this.clientEmailDraft = client.email ?? '';
        this.applyClientAccountToSelectedUser(client);
        this.loadSelectedClientTelegramLink(client.id);
        this.isClientAccountLoading = false;
      },
      error: (error: unknown) => {
        this.clientAccount = null;
        this.clientEmailDraft = '';
        this.resetSelectedClientTelegramState();
        this.clientAccountActionErrorText = resolveHttpErrorMessageKey(error, {
          fallback: 'Failed to load client account',
        });
        this.isClientAccountLoading = false;
      },
    });
  }

  private loadSelectedClientTelegramLink(clientId: string): void {
    this.resetSelectedClientTelegramState();

    if (!this.canManageSelectedClientTelegramLink) {
      return;
    }

    this.isClientTelegramLoading = true;
    this.telegramConnectorService.getClientLink(
      clientId,
      this.getContextCompanyId() ?? undefined,
    ).subscribe({
      next: (link) => {
        this.clientTelegramLink = link;
        this.syncClientTelegramCommunicationChannel(link);
        this.isClientTelegramLoading = false;
      },
      error: () => {
        this.clientTelegramLink = null;
        this.isClientTelegramLoading = false;
      },
    });
  }

  private syncClientTelegramCommunicationChannel(link: TelegramLinkDto | null): void {
    if (!this.clientAccount) {
      return;
    }

    const currentChannels = this.clientAccount.communicationChannels;
    const telegramStatus = link?.status ?? 'unlinked';
    const nextChannels: ClientCommunicationChannels = {
      ...currentChannels,
      telegram: {
        status: telegramStatus,
        deliveryMode: telegramStatus === 'active' ? 'automatic' : 'none',
        username: link?.telegramUsername ?? null,
        linkedAt: link?.linkedAt ?? null,
        revokedAt: link?.revokedAt ?? null,
        blockedAt: link?.blockedAt ?? null,
        lastDeliveryFailedAt: link?.lastDeliveryFailedAt ?? null,
        lastDeliveryError: link?.lastDeliveryError ?? null,
      },
    };
    const automaticChannels = new Set(nextChannels.summary.automaticChannels);

    if (telegramStatus === 'active') {
      automaticChannels.add('telegram');
    } else {
      automaticChannels.delete('telegram');
    }

    const automaticChannelList = Array.from(automaticChannels);
    const manualChannels = nextChannels.summary.manualChannels;

    this.clientAccount = {
      ...this.clientAccount,
      communicationChannels: {
        ...nextChannels,
        summary: {
          automaticReachable: automaticChannelList.length > 0,
          manualReachable: manualChannels.length > 0,
          manualFollowUpRequired: automaticChannelList.length === 0 && manualChannels.length > 0,
          reachabilityStatus: automaticChannelList.length > 0
            ? 'automatic'
            : manualChannels.length > 0
              ? 'manual'
              : 'unreachable',
          automaticChannels: automaticChannelList,
          manualChannels,
        },
      },
    };
  }

  private loadClientRatingHistory(): void {
    const targetId = this.selectedUser?.role === 'client'
      ? this.selectedUser.id
      : '';

    if (!targetId) {
      this.clientRatingHistory = [];
      this.isClientRatingHistoryLoading = false;
      return;
    }

    this.isClientRatingHistoryLoading = true;
    this.usersService.getClientRatingEvents(
      targetId,
      this.getContextCompanyId() ?? undefined,
    ).subscribe({
      next: (events) => {
        this.clientRatingHistory = events;
        this.isClientRatingHistoryLoading = false;
      },
      error: () => {
        this.clientRatingHistory = [];
        this.isClientRatingHistoryLoading = false;
      },
    });
  }

  private resetClientAccountState(): void {
    this.clientAccount = null;
    this.clientRatingHistory = [];
    this.isClientAccountLoading = false;
    this.isClientRatingHistoryLoading = false;
    this.isClientRatingAdjusting = false;
    this.isClientEmailSaving = false;
    this.isClientInviteSending = false;
    this.resetSelectedClientTelegramState();
    this.clientEmailDraft = '';
    this.clientAccountActionErrorText = null;
    this.clientAccountSuccessText = null;
    this.clientRatingAdjustmentErrorText = null;
    this.clientRatingAdjustmentSuccessText = null;
    this.clientRatingDeltaDraft = 0;
    this.clientRatingNoteDraft = '';
  }

  private resetSelectedClientTelegramState(): void {
    this.clientTelegramLink = null;
    this.clientTelegramLinkToken = null;
    this.clientTelegramQrDataUrl = null;
    this.isClientTelegramLoading = false;
    this.isClientTelegramTokenCreating = false;
    this.isClientTelegramLinkCopying = false;
    this.isClientTelegramRevoking = false;
  }

  private async updateSelectedClientTelegramQr(linkUrl: string): Promise<void> {
    try {
      this.clientTelegramQrDataUrl = await toDataURL(linkUrl, {
        errorCorrectionLevel: 'M',
        margin: 2,
        width: 180,
      });
    } catch {
      this.clientTelegramQrDataUrl = null;
    } finally {
      this.changeDetectorRef.detectChanges();
    }
  }

  private resolveTelegramError(error: unknown): string {
    return resolveHttpErrorMessageKey(error, {
      fallback: 'error.generic',
      forbidden: 'error.forbiddenResource',
      badRequestByErrorCode: {
        telegram_webhook_not_configured: 'telegram.error.webhookNotConfigured',
      },
    });
  }

  private resetStaffAccountState(): void {
    this.isStaffEmailSaving = false;
    this.isStaffInviteSending = false;
    this.staffEmailDraft = '';
    this.staffAccountActionErrorText = null;
    this.staffAccountSuccessText = null;
  }

  private resetSelectedUserPhotoState(): void {
    this.selectedUserPhotoFile = null;
    this.selectedUserPhotoName = '';
    this.isUserPhotoUploading = false;
    this.isUserPhotoRemoving = false;
  }

  private rebindAvatarVisibilityObserver(): void {
    queueMicrotask(() => {
      if (!this.avatarSentinels) {
        return;
      }

      if (!this.avatarVisibilityObserver && typeof IntersectionObserver !== 'undefined') {
        this.avatarVisibilityObserver = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) {
              continue;
            }
            const userId = (entry.target as HTMLElement).dataset['userId']?.trim() ?? '';
            if (!userId) {
              continue;
            }
            this.visibleAvatarIds.add(userId);
            this.avatarVisibilityObserver?.unobserve(entry.target);
          }
        }, {
          root: null,
          rootMargin: '120px',
          threshold: 0.01,
        });
      }

      if (!this.avatarVisibilityObserver) {
        return;
      }

      this.avatarSentinels.forEach((sentinel) => {
        const userId = sentinel.nativeElement.dataset['userId']?.trim() ?? '';
        if (!userId || this.visibleAvatarIds.has(userId)) {
          return;
        }
        this.avatarVisibilityObserver?.observe(sentinel.nativeElement);
      });
    });
  }

  private applyClientAccountToSelectedUser(client: ClientAdminItem): void {
    if (this.selectedUser?.role !== 'client') {
      return;
    }

    this.selectedUser = {
      ...this.selectedUser,
      photoUrl: client.photoUrl ?? this.selectedUser.photoUrl,
      fullName: client.fullName || this.selectedUser.fullName,
      birthDate: client.birthDate ?? this.selectedUser.birthDate,
      email: client.email ?? this.selectedUser.email,
      phone: client.phone ?? this.selectedUser.phone,
      accountStatus: client.accountStatus,
      inviteExpiresAt: client.inviteExpiresAt,
      accountOrigin: client.accountOrigin,
      clientProfile: this.selectedUser.clientProfile
        ? {
            ...this.selectedUser.clientProfile,
            fullName: client.fullName,
            birthDate: client.birthDate,
            notes: client.notes,
            ratingPercent: client.ratingPercent,
          }
        : this.selectedUser.clientProfile,
    };
    this.syncSelectedUserIntoItems();
    this.form.patchValue({
      fullName: this.selectedUser.fullName,
      phone: this.selectedUser.phone ?? '',
      birthDate: this.normalizeBirthDateForInput(this.selectedUser.birthDate ?? null),
      notes: this.selectedUser.clientProfile?.notes ?? '',
    }, { emitEvent: false });
  }

  private syncSelectedUserIntoItems(): void {
    if (!this.selectedUser) {
      return;
    }

    this.items = this.items.map((item) =>
      item.id === this.selectedUser?.id ? this.selectedUser : item,
    );
  }
}
